import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation, useParams } from 'react-router-dom';
import { loginPathFor, nextFromSearch } from './lib/post-login-target';
import { QueryClient, QueryClientProvider, useQueryClient } from '@tanstack/react-query';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import SalesRouteGate from './components/SalesRouteGate';
import { isSalesRole } from './lib/sales-access';
import { IndustryProvider } from './contexts/IndustryContext';
import { LocaleProvider } from './contexts/LocaleContext';
import { TenantZoneGate } from './components/TenantZoneGate';
import { ThemeProvider } from './context/ThemeContext';
import { initOnlineDetection, destroyOnlineDetection } from './lib/offline/online-detector';
import { performSync } from './lib/offline/sync-engine';
import { refreshSyncCounts } from './lib/offline/sync-counts';
import { useSyncStore } from './lib/offline/sync-store';
import { App as CapApp } from '@capacitor/app';
import { StatusBar, Style } from '@capacitor/status-bar';
import { SplashScreen } from '@capacitor/splash-screen';
import { isNative } from './lib/platform';
import PushInit from './components/push/PushInit';
import PushPrompt from './components/push/PushPrompt';
import { isInternalRole } from './lib/roles';
import { JobDetail } from './pages/jobs/JobDetail';
import { TicketDetail } from './pages/tickets/TicketDetail';
import { EmployeeProfile } from './pages/employees/EmployeeProfile';
import { EquipmentDetail } from './pages/equipment/EquipmentDetail';
import { RateSheetDetail } from './pages/rates/RateSheetDetail';
import { CustomerDetail } from './pages/customers/CustomerDetail';
import { SafetyForm } from './pages/safety/SafetyForm';
import { VideoPlayer } from './pages/training/VideoPlayer';
import { ReportsDashboard } from './pages/reports/ReportsDashboard';
import UnifiedReports from './pages/reports/UnifiedReports';
import EmployeeHoursDetailScreen from './pages/reports/EmployeeHoursDetailScreen';
import CasingFinancialReport from './pages/reports/CasingFinancialReport';
import TrainingDashboard from './screens/training/TrainingDashboard';
import CourseDetail from './screens/training/CourseDetail';
import TrainingVideoPlayer from './screens/training/TrainingVideoPlayer';
import TroubleshootingGuide from './screens/training/TroubleshootingGuide';
// Screens
import LoginScreen from './screens/LoginScreen';
import ForgotPasswordScreen from './screens/ForgotPasswordScreen';
import ResetPasswordScreen from './screens/ResetPasswordScreen';
import InviteSetupScreen from './screens/InviteSetupScreen';
import { AppLayout } from './screens/HomeScreen';
import OverviewScreen from './screens/OverviewScreen';
import SyncStatusBar from './components/SyncStatusBar';
import SettingsLayout from './pages/settings/SettingsLayout';
import LearnedRulesPage from './pages/settings/LearnedRulesPage';
import BillingAgentPage from './pages/settings/BillingAgentPage';
import ScoutAgentPage from './pages/settings/ScoutAgentPage';
import MaintenanceAgentPage from './pages/settings/MaintenanceAgentPage';
import SettingsIndexPage from './pages/settings/SettingsIndexPage';
import CompanyProfilePage from './pages/settings/CompanyProfilePage';
import LocationsPage from './pages/settings/LocationsPage';
import RegionalTaxPage from './pages/settings/RegionalTaxPage';
import AppearancePage from './pages/settings/AppearancePage';
import InvoiceSettingsPage from './pages/settings/InvoiceSettingsPage';
import FieldTicketsPage from './pages/settings/FieldTicketsPage';
import JobNumberingPage from './pages/settings/JobNumberingPage';
import TruckingSettingsPage from './pages/settings/TruckingSettingsPage';
import RentalsSettingsPage from './pages/settings/RentalsSettingsPage';
import ServicesPage from './pages/settings/ServicesPage';
import EquipmentTypesPage from './pages/settings/EquipmentTypesPage';
import PositionsPage from './pages/settings/PositionsPage';
import LineItemTypesPage from './pages/settings/LineItemTypesPage';
import SettingsPlaceholder from './pages/settings/SettingsPlaceholder';
import IntegrationsPage from './pages/settings/IntegrationsPage';
import QuickBooksMappingPage from './pages/settings/QuickBooksMappingPage';
import GpsConnectorsPage from './pages/settings/GpsConnectorsPage';
import EmailBrandingPage from './pages/settings/EmailBrandingPage';
import DashboardLayoutPage from './pages/settings/DashboardLayoutPage';
import RolesPermissionsPage from './pages/settings/RolesPermissionsPage';
import AIAgentSettingsPage from './pages/settings/AIAgentSettingsPage';
import SafetyMetricsSettingsPage from './pages/settings/SafetyMetricsSettingsPage';
import SafetyWorkspace from './pages/settings/SafetyWorkspace';
import SettingsWorkspaceTabs from './pages/settings/SettingsWorkspaceTabs';
import PayPeriodSettingsScreen from './screens/payroll/PayPeriodSettingsScreen';
import HelpVideosScreen from './pages/HelpVideosScreen';
import JobsScreen from './screens/JobsScreen';
import DeletedJobsScreen from './screens/DeletedJobsScreen';
import DispatchScreen from './screens/DispatchScreen';
import TicketsScreen from './screens/TicketsScreen';
import MoreScreen from './screens/MoreScreen';
import NotificationPreferencesScreen from './screens/NotificationPreferencesScreen';
import ChatNotificationToast from './components/ChatNotificationToast';
import GlobalChatToast from './components/GlobalChatToast';
import { useGlobalChat } from './hooks/useGlobalChat';
import { useScrollRestoration } from './hooks/useScrollRestoration';
import FieldHomeScreen from './screens/field/FieldHomeScreen';
import FieldJobsScreen from './screens/field/FieldJobsScreen';
import FieldJobDetail from './screens/field/FieldJobDetail';
import FieldJobEquipmentScreen from './screens/field/FieldJobEquipmentScreen';
import FieldTicketsScreen from './screens/field/FieldTicketsScreen';
import FieldEquipmentScreen from './screens/field/FieldEquipmentScreen';
import FieldEquipmentCheckForm from './screens/field/FieldEquipmentCheckForm';
import FieldEquipmentDetail from './screens/field/FieldEquipmentDetail';
import FieldCertsScreen from './screens/field/FieldCertsScreen';
import FieldPayScreen from './screens/field/FieldPayScreen';
import FieldProfileScreen from './screens/field/FieldProfileScreen';
import FieldMoreScreen from './screens/field/FieldMoreScreen';
import FieldFormsScreen from './screens/field/FieldFormsScreen';
import FieldTimeOffScreen from './screens/field/FieldTimeOffScreen';
import FieldSafetyScreen from './screens/field/FieldSafetyScreen';
import FieldExpensesScreen from './screens/field/FieldExpensesScreen';
import FieldExpenseCaptureScreen from './screens/field/FieldExpenseCaptureScreen';
import ClockInScreen from './screens/payroll/ClockInScreen';
import OwnShiftEditScreen from './screens/payroll/OwnShiftEditScreen';
import MissedShiftScreen from './screens/payroll/MissedShiftScreen';
import ApprovalQueueScreen from './screens/payroll/ApprovalQueueScreen';
import TimesheetApprovalScreen from './screens/payroll/TimesheetApprovalScreen';
import WorkerListScreen from './screens/payroll/WorkerListScreen';
import WorkerPayProfileScreen from './screens/payroll/WorkerPayProfileScreen';
import PayrollSummaryScreen from './screens/payroll/PayrollSummaryScreen';
import SafetyScreen from './screens/SafetyScreen';
import SafetyRecordsScreen from './screens/SafetyRecordsScreen';
import SafetyLayout from './pages/safety/SafetyLayout';
import NewJobScreen from './screens/NewJobScreen';
import CrewAssignmentScreen from './screens/CrewAssignmentScreen';
import EquipmentAssignmentScreen from './screens/EquipmentAssignmentScreen';
import EquipmentScreen from './screens/EquipmentScreen';
import ServiceLocationsScreen from './screens/ServiceLocationsScreen';
import EquipmentCertScreen from './screens/EquipmentCertScreen';
import PreuseTemplatesScreen from './pages/equipment/PreuseTemplatesScreen';
import PreuseTemplateNewScreen from './pages/equipment/PreuseTemplateNewScreen';
import PreuseTemplateDetailScreen from './pages/equipment/PreuseTemplateDetailScreen';
import PressureControlScreen from './pages/equipment/PressureControlScreen';
import PressureControlDetailScreen from './pages/equipment/PressureControlDetailScreen';
import PressureControlTestsScreen from './pages/equipment/PressureControlTestsScreen';
import PressureControlTestNewScreen from './pages/equipment/PressureControlTestNewScreen';
import PressureControlTestDetailScreen from './pages/equipment/PressureControlTestDetailScreen';
import PressureControlDrillsScreen from './pages/equipment/PressureControlDrillsScreen';
import PressureControlDrillDetailScreen from './pages/equipment/PressureControlDrillDetailScreen';
import PressureControlCalibrationsScreen from './pages/equipment/PressureControlCalibrationsScreen';
import PressureControlGaugeScreen from './pages/equipment/PressureControlGaugeScreen';
import RadiationScreen from './pages/equipment/RadiationScreen';
import RadiationSourceScreen from './pages/equipment/RadiationSourceScreen';
import RadiationDosimetryScreen from './pages/equipment/RadiationDosimetryScreen';
import CertDayImportScreen from './screens/CertDayImportScreen';
import TeamScreen from './screens/TeamScreen';
import PayrollScreen from './screens/PayrollScreen';
import ExpenseApprovalScreen from './screens/ExpenseApprovalScreen';
import OfficeExpenseCaptureScreen from './screens/OfficeExpenseCaptureScreen';
import RateSheetsScreen from './screens/RateSheetsScreen';
import PriceBookMissingScreen from './screens/PriceBookMissingScreen';
import CustomersScreen from './screens/CustomersScreen';
import CrmRouteGate from './components/CrmRouteGate';
import MeetingsScreen from './pages/sales/MeetingsScreen';
import MeetingFormScreen from './pages/sales/MeetingFormScreen';
import MeetingDetail from './pages/sales/MeetingDetail';
import PipelineScreen from './pages/sales/PipelineScreen';
import OpportunityDetail from './pages/sales/OpportunityDetail';
import CustomerReportScreen from './screens/CustomerReportScreen';
import EquipmentMaintenanceReportScreen from './screens/EquipmentMaintenanceReportScreen';
import NewTicketScreen from './screens/NewTicketScreen';
import PayAssignmentScreen from './screens/PayAssignmentScreen';
import TimeOffScreen from './screens/TimeOffScreen';
import InvoicesScreen from './screens/InvoicesScreen';
import InvoiceDetailScreen from './screens/InvoiceDetailScreen';
import InvoiceGenerateScreen from './screens/InvoiceGenerateScreen';
import InvoiceEditScreen from './screens/InvoiceEditScreen';
import QuotesScreen from './screens/QuotesScreen';
import QuoteCreateScreen from './screens/QuoteCreateScreen';
import QuoteDetailScreen from './screens/QuoteDetailScreen';
import QuoteEditScreen from './screens/QuoteEditScreen';
import AIAgentScreen from './screens/AIAgentScreen';
import ImportScreen from './screens/ImportScreen';
import AlertsScreen from './screens/AlertsScreen';
import DriverBOLScreen from './screens/field/trucking/DriverBOLScreen';
import DriverMasterBolScreen from './screens/field/trucking/DriverMasterBolScreen';
import DriverWeightTicketScreen from './screens/field/trucking/DriverWeightTicketScreen';
import DriverProfileScreen from './screens/field/trucking/DriverProfileScreen';
import DriverBOLsListScreen from './screens/field/trucking/DriverBOLsListScreen';
import TruckingDispatch from './pages/TruckingDispatch';
import DispatchCalendar from './pages/DispatchCalendar';
import NewLoadBooking from './pages/NewLoadBooking';
import DriverLoadView from './pages/DriverLoadView';
import WeightTicketView from './pages/WeightTicketView';

// Rentals
import RentalDispatchBoard from './pages/rentals/RentalDispatchBoard';
import ActiveRentals from './pages/rentals/ActiveRentals';
import AssetRegistry from './pages/rentals/AssetRegistry';
import RentalAssetDetailScreen from './pages/rentals/AssetDetailScreen';
import RentalYard from './pages/rentals/RentalYard';
import RentalFieldTickets from './pages/rentals/RentalFieldTickets';
import RentalFieldTicketDetail from './pages/rentals/RentalFieldTicketDetail';
import MaintenanceWorkOrders from './pages/rentals/MaintenanceWorkOrders';
import RentalWorkOrderDetail from './pages/rentals/RentalWorkOrderDetail';
import RentalOrderDetail from './pages/rentals/RentalOrderDetail';
import RentalInvoiceDetail from './pages/rentals/RentalInvoiceDetail';
import RentalDispatchDetail from './pages/rentals/RentalDispatchDetail';

// Report pages
import RevenueSummaryReport from './pages/reports/RevenueSummaryReport';
import RevenueByAssetReport from './pages/reports/RevenueByAssetReport';
import CrossCustomerReport from './pages/reports/CustomerReport';
import YardMovementReport from './pages/reports/YardMovementReport';
import MaintenanceSummaryReport from './pages/reports/MaintenanceSummaryReport';
import PayrollSummaryReport from './pages/reports/PayrollSummaryReport';
import DriverPayDetailReport from './pages/reports/DriverPayDetailReport';
import PriceBooks from './pages/rentals/PriceBooks';
import RentalInspections from './pages/rentals/RentalInspections';
import RentalInspectionDetail from './pages/rentals/RentalInspectionDetail';
import RentalInspectionDraft from './pages/rentals/RentalInspectionDraft';
import RentalDamageCharges from './pages/rentals/RentalDamageCharges';

// Rental field tech screens
import DeliveryConfirmation from './screens/field/rentals/DeliveryConfirmation';
import ReturnConditionCheck from './screens/field/rentals/ReturnConditionCheck';

import MoveJobsScreen from './pages/trucking/MoveJobsScreen';
import MoveJobDetailScreen from './pages/trucking/MoveJobDetailScreen';
import TruckingReportsScreen from './pages/trucking/TruckingReportsScreen';
import CasingIntakeScreen from './pages/casing/CasingIntakeScreen';
import FinanceInvoices from './pages/finance/Invoices';
import BolDocumentView from './pages/BolDocumentView';
import BillsOfLading from './pages/trucking/BillsOfLading';
import BillOfLadingDetail from './pages/trucking/BillOfLadingDetail';
import TrucksScreen from './screens/trucking/TrucksScreen';
import DriverManagementScreen from './screens/trucking/DriverManagementScreen';
import FleetUnitPage from './pages/trucking/FleetUnitPage';
import DriverPage from './pages/trucking/DriverPage';
// FleetRegistryScreen and FleetExpensesScreen are now embedded tabs in TrucksScreen (Fleet)
import TeamCertsScreen from './screens/TeamCertsScreen';
import CertificationReviewQueue from './pages/CertificationReviewQueue';
import PortalDashboard from './screens/portal/PortalDashboard';
import TicketReviewPage from './pages/TicketReviewPage';
import QuoteReviewPage from './pages/QuoteReviewPage';
import InvoicePayPage from './pages/InvoicePayPage';
import ChangeOrderReviewPage from './pages/ChangeOrderReviewPage';
import RentalPortalPage from './pages/RentalPortalPage';
import PortalInvoices from './screens/portal/PortalInvoices';
import PortalInvoiceDetail from './screens/portal/PortalInvoiceDetail';
import PortalQuotes from './screens/portal/PortalQuotes';
import PortalQuoteDetail from './screens/portal/PortalQuoteDetail';
import PortalProfile from './screens/portal/PortalProfile';

// Downhole Tools — rebuilt on dh_* (fieldiq-api#882). See src/pages/downhole/README.md.
import DownholeDashboardScreen from './pages/downhole/DashboardScreen';
import DownholeComponentsScreen from './pages/downhole/ComponentsScreen';
import DownholeComponentDetailScreen from './pages/downhole/ComponentDetailScreen';
import DownholeAssembliesScreen from './pages/downhole/AssembliesScreen';
import DownholeWellsScreen from './pages/downhole/WellsScreen';
import DownholeWellDetailScreen from './pages/downhole/WellDetailScreen';
import DownholeAssemblyBuilderScreen from './pages/downhole/AssemblyBuilderScreen';
import DownholeTemplatesScreen from './pages/downhole/TemplatesScreen';
import DownholeTemplateDetailScreen from './pages/downhole/TemplateDetailScreen';
import DownholeRunLogsScreen from './pages/downhole/RunLogsScreen';
import DownholeRunLogDetailScreen from './pages/downhole/RunLogDetailScreen';
import DownholeWorkOrdersScreen from './pages/downhole/WorkOrdersScreen';
import DownholeWorkOrderDetailScreen from './pages/downhole/WorkOrderDetailScreen';
import DownholeInvoicesScreen from './pages/downhole/InvoicesScreen';
import DownholeReadyToInvoiceScreen from './pages/downhole/ReadyToInvoiceScreen';
import DownholeInvoiceDetailScreen from './pages/downhole/InvoiceDetailScreen';
import DownholeReportsScreen from './pages/downhole/ReportsScreen';
import DownholeRatesScreen from './pages/downhole/RatesScreen';
import TruckingRatesScreen from './pages/trucking/TruckingRatesScreen';
import TruckingInvoiceDetail from './pages/trucking/TruckingInvoiceDetail';
import DownholeTeardownScreen from './pages/downhole/TeardownScreen';
import DownholeTicketsScreen from './pages/downhole/TicketsScreen';
import DownholeTicketDetailScreen from './pages/downhole/TicketDetailScreen';
// Pressure Testing — instruments and calibrations (fieldiq-api#907).
import PtInstrumentsScreen from './pages/pressure-testing/InstrumentsScreen';
import PtInstrumentDetailScreen from './pages/pressure-testing/InstrumentDetailScreen';
import PtCalibrationAlertsScreen from './pages/pressure-testing/CalibrationAlertsScreen';
// Pressure Testing bundle 2 — test jobs and tests (fieldiq-api#915).
import PtTestJobsScreen from './pages/pressure-testing/TestJobsScreen';
import PtTestJobDetailScreen from './pages/pressure-testing/TestJobDetailScreen';
import PtCertificatesScreen from './pages/pressure-testing/CertificatesScreen';
import PtFieldTicketsScreen from './pages/pressure-testing/FieldTicketsScreen';
import PtInvoicesScreen from './pages/pressure-testing/InvoicesScreen';
import PtCertificateDetailScreen from './pages/pressure-testing/CertificateDetailScreen';
import PtFieldTicketDetailScreen from './pages/pressure-testing/FieldTicketDetailScreen';
import PtInvoiceDetailScreen from './pages/pressure-testing/InvoiceDetailScreen';
import PtMyJobsScreen from './pages/pressure-testing/field/PtMyJobsScreen';
import PtFieldJobScreen from './pages/pressure-testing/field/PtFieldJobScreen';
import PtFieldTestScreen from './pages/pressure-testing/field/PtFieldTestScreen';
import PtFieldTicketScreen from './pages/pressure-testing/field/PtFieldTickets';
import PressureTestingSettingsPage from './pages/settings/PressureTestingSettingsPage';
// NDT / Inspections batch 1 — people, instruments, radiation (fieldiq-api#1134).
import NdtTechniciansScreen from './pages/ndt-inspection/TechniciansScreen';
import NdtTechnicianCertDetailScreen from './pages/ndt-inspection/TechnicianCertDetailScreen';
import NdtWeldersScreen from './pages/ndt-inspection/WeldersScreen';
import NdtWelderDetailScreen from './pages/ndt-inspection/WelderDetailScreen';
import NdtInstrumentsScreen from './pages/ndt-inspection/InstrumentsScreen';
import NdtInstrumentDetailScreen from './pages/ndt-inspection/InstrumentDetailScreen';
import NdtSourcesScreen from './pages/ndt-inspection/SourcesScreen';
import NdtSourceDetailScreen from './pages/ndt-inspection/SourceDetailScreen';
import NdtDosimetryScreen from './pages/ndt-inspection/DosimetryScreen';
import NdtSettingsPage from './pages/settings/NdtSettingsPage';
// NDT / Inspections batch 2 — jobs, welds, exams; and the technician's field flow (fieldiq-api#1144).
import NdtJobsScreen from './pages/ndt-inspection/JobsScreen';
import NdtJobDetailScreen from './pages/ndt-inspection/JobDetailScreen';
import NdtWeldsScreen from './pages/ndt-inspection/WeldsScreen';
import NdtWeldDetailScreen from './pages/ndt-inspection/WeldDetailScreen';
import NdtReportsScreen from './pages/ndt-inspection/ReportsScreen';
import NdtReportDetailScreen from './pages/ndt-inspection/ReportDetailScreen';
import NdtMyJobsScreen from './pages/ndt-inspection/field/NdtMyJobsScreen';
import NdtFieldJobScreen from './pages/ndt-inspection/field/NdtFieldJobScreen';
import NdtFieldWeldScreen from './pages/ndt-inspection/field/NdtFieldWeldScreen';
import NdtTicketsScreen from './pages/ndt-inspection/TicketsScreen';
import NdtTicketDetailScreen from './pages/ndt-inspection/TicketDetailScreen';
import NdtInvoicesScreen from './pages/ndt-inspection/InvoicesScreen';
import NdtInvoiceDetailScreen from './pages/ndt-inspection/InvoiceDetailScreen';
import NdtFieldTicketScreen from './pages/ndt-inspection/field/NdtFieldTickets';
import NdtAiDigestsScreen from './pages/ndt-inspection/AiDigestsScreen';
import NdtAiDigestDetailScreen from './pages/ndt-inspection/AiDigestDetailScreen';
// Pipeline & Facility PF-1 — sites + segments, settings (fieldiq-api#1196).
import PfSitesScreen from './pages/pipeline-facility/SitesScreen';
import PfNewSiteScreen from './pages/pipeline-facility/NewSiteScreen';
import PfSiteDetailScreen from './pages/pipeline-facility/SiteDetailScreen';
import PfJobsScreen from './pages/pipeline-facility/JobsScreen';
import PfNewJobScreen from './pages/pipeline-facility/NewJobScreen';
import PfJobDetailScreen from './pages/pipeline-facility/JobDetailScreen';
import PfDailyLogScreen from './pages/pipeline-facility/DailyLogScreen';
import PfMyJobsScreen from './pages/pipeline-facility/field/PfMyJobsScreen';
import PfFieldJobScreen from './pages/pipeline-facility/field/PfFieldJobScreen';
import PfFieldLogScreen from './pages/pipeline-facility/field/PfFieldLogScreen';
import PfAnomaliesScreen from './pages/pipeline-facility/digs/AnomaliesScreen';
import PfAnomalyDetailScreen, { PfNewAnomalyScreen } from './pages/pipeline-facility/digs/AnomalyDetailScreen';
import PfAnomalyImportScreen from './pages/pipeline-facility/digs/AnomalyImportScreen';
import PfDigsScreen from './pages/pipeline-facility/digs/DigsScreen';
import PfDigDetailScreen from './pages/pipeline-facility/digs/DigDetailScreen';
import PfNewDigScreen from './pages/pipeline-facility/digs/NewDigScreen';
import PfWeldsScreen from './pages/pipeline-facility/digs/WeldsScreen';
import PfWeldDetailScreen, { PfNewWeldScreen } from './pages/pipeline-facility/digs/WeldDetailScreen';
import PfReportsScreen, { PfReportDetailScreen } from './pages/pipeline-facility/digs/ReportsScreen';
import { PfFieldDigScreen, PfMyDigsScreen } from './pages/pipeline-facility/field/PfFieldDigScreens';
import PfRatesScreen from './pages/pipeline-facility/tickets/RatesScreen';
import PfRateDetailScreen, { PfNewRateScreen } from './pages/pipeline-facility/tickets/RateDetailScreen';
import PfTicketsScreen from './pages/pipeline-facility/tickets/TicketsScreen';
import PfTicketDetailScreen from './pages/pipeline-facility/tickets/TicketDetailScreen';
import PfInvoicesScreen from './pages/pipeline-facility/invoices/InvoicesScreen';
import PfReadyToInvoiceScreen from './pages/pipeline-facility/invoices/ReadyToInvoiceScreen';
import PfInvoiceDetailScreen from './pages/pipeline-facility/invoices/InvoiceDetailScreen';
import PfAiDraftsScreen from './pages/pipeline-facility/ai/AiDraftsScreen';
import PfAiDraftDetailScreen from './pages/pipeline-facility/ai/AiDraftDetailScreen';
import PipelineFacilitySettingsPage from './pages/settings/PipelineFacilitySettingsPage';
// Well Testing WT-1 (fieldiq-api#1241): the wells register and its settings.
import WtWellsScreen from './pages/well-testing/WellsScreen';
import WtNewWellScreen from './pages/well-testing/NewWellScreen';
import WtWellDetailScreen from './pages/well-testing/WellDetailScreen';
import WellTestingSettingsPage from './pages/settings/WellTestingSettingsPage';
import PressureControlSettingsPage from './pages/settings/PressureControlSettingsPage';
import RadiationSettingsPage from './pages/settings/RadiationSettingsPage';
import WtJobsScreen from './pages/well-testing/JobsScreen';
import WtNewJobScreen from './pages/well-testing/NewJobScreen';
import WtJobDetailScreen from './pages/well-testing/JobDetailScreen';
import WtShiftLogScreen from './pages/well-testing/ShiftLogScreen';
import WtLoggerImportScreen from './pages/well-testing/LoggerImportScreen';
import WtPackagesScreen from './pages/well-testing/PackagesScreen';
import WtPackageDetailScreen from './pages/well-testing/PackageDetailScreen';
import WtMyJobsScreen from './pages/well-testing/field/WtMyJobsScreen';
import WtFieldJobScreen from './pages/well-testing/field/WtFieldJobScreen';
import WtFieldShiftLogScreen from './pages/well-testing/field/WtFieldShiftLogScreen';
import WtFieldReadingScreen from './pages/well-testing/field/WtFieldReadingScreen';
import WtRatesScreen from './pages/well-testing/tickets/RatesScreen';
import WtRateDetailScreen, { WtNewRateScreen } from './pages/well-testing/tickets/RateDetailScreen';
import WtTicketsScreen from './pages/well-testing/tickets/TicketsScreen';
import WtTicketDetailScreen from './pages/well-testing/tickets/TicketDetailScreen';
import WtInvoicesScreen from './pages/well-testing/invoices/InvoicesScreen';
import WtNewInvoiceScreen from './pages/well-testing/invoices/NewInvoiceScreen';
import WtInvoiceDetailScreen from './pages/well-testing/invoices/InvoiceDetailScreen';
import WtAiDraftsScreen from './pages/well-testing/ai/AiDraftsScreen';
import WtAiDraftDetailScreen from './pages/well-testing/ai/AiDraftDetailScreen';
// Earthworks EW-1: jobs, sites, the rate card and its settings.
import EwJobsScreen from './pages/earthworks/JobsScreen';
import EwNewJobScreen from './pages/earthworks/NewJobScreen';
import EwJobDetailScreen from './pages/earthworks/JobDetailScreen';
import EwEmployeeHoursScreen from './pages/earthworks/EwEmployeeHours';
import EwSitesScreen from './pages/earthworks/SitesScreen';
import EwNewSiteScreen from './pages/earthworks/NewSiteScreen';
import EwSiteDetailScreen from './pages/earthworks/SiteDetailScreen';
import EwRatesScreen from './pages/earthworks/RatesScreen';
import EwInventoryScreen from './pages/earthworks/inventory/InventoryScreen';
import EwMaterialDetailScreen, { EwNewMaterialScreen } from './pages/earthworks/inventory/MaterialDetailScreen';
import EwMaterialLocationsScreen, { EwMaterialLocationScreen } from './pages/earthworks/inventory/LocationsScreen';
import EwRateDetailScreen, { EwNewRateScreen } from './pages/earthworks/RateDetailScreen';
import EwAiQuotesScreen from './pages/earthworks/AiQuotesScreen';
import EwNewAiQuoteScreen from './pages/earthworks/NewAiQuoteScreen';
import EwAiQuoteDetailScreen from './pages/earthworks/AiQuoteDetailScreen';
// Earthworks EW-8: quoted vs actual, by job type.
import EwQvaScreen from './pages/earthworks/qva/EwQvaScreen';
import EwQvaJobTypeScreen from './pages/earthworks/qva/EwQvaJobTypeScreen';
// Earthworks EW-4: the field (phone) and the office daily-log record.
import EwDailyLogScreen from './pages/earthworks/DailyLogScreen';
import EwOfficeLogEditScreen from './pages/earthworks/OfficeLogEditScreen';
import EwMyJobsScreen from './pages/earthworks/field/EwMyJobsScreen';
import EwFieldJobScreen from './pages/earthworks/field/EwFieldJobScreen';
import EwFieldLogScreen from './pages/earthworks/field/EwFieldLogScreen';
import EwFieldTicketScreen from './pages/earthworks/field/EwFieldTicketScreen';
import EwFieldCoScreen from './pages/earthworks/field/EwFieldCoScreen';
import EwTicketsScreen from './pages/earthworks/EwTicketsScreen';
import EwTicketDetailScreen from './pages/earthworks/EwTicketDetailScreen';
import EwChangeOrdersScreen from './pages/earthworks/EwChangeOrdersScreen';
import EwChangeOrderDetailScreen from './pages/earthworks/EwChangeOrderDetailScreen';
import EwReadyToInvoiceScreen from './pages/earthworks/invoices/EwReadyToInvoiceScreen';
import EwInvoiceDetailScreen from './pages/earthworks/invoices/EwInvoiceDetailScreen';
import AiUsagePage from './pages/settings/AiUsagePage';
import EarthworksSettingsPage from './pages/settings/EarthworksSettingsPage';
// Well Servicing WS-1: the wells register, rigs + crew templates, activity codes, settings.
import WsWellsScreen from './pages/well-servicing/WellsScreen';
import WsNewWellScreen from './pages/well-servicing/NewWellScreen';
import WsWellDetailScreen from './pages/well-servicing/WellDetailScreen';
import WsRigsScreen from './pages/well-servicing/RigsScreen';
import WsNewRigScreen from './pages/well-servicing/NewRigScreen';
import WsRigDetailScreen from './pages/well-servicing/RigDetailScreen';
import WsActivityCodesScreen from './pages/well-servicing/ActivityCodesScreen';
import WsJobsScreen from './pages/well-servicing/JobsScreen';
import WsNewJobScreen from './pages/well-servicing/NewJobScreen';
import WsJobDetailScreen from './pages/well-servicing/JobDetailScreen';
import WsTourScreen from './pages/well-servicing/TourScreen';
import WsRatesScreen from './pages/well-servicing/tickets/RatesScreen';
import WsRateDetailScreen, { WsNewRateScreen } from './pages/well-servicing/tickets/RateDetailScreen';
import WsTicketsScreen from './pages/well-servicing/tickets/TicketsScreen';
import WsTicketDetailScreen from './pages/well-servicing/tickets/TicketDetailScreen';
import WsInvoicesScreen from './pages/well-servicing/invoices/InvoicesScreen';
import WsNewInvoiceScreen from './pages/well-servicing/invoices/NewInvoiceScreen';
import WsReadyToInvoiceScreen from './pages/well-servicing/invoices/ReadyToInvoiceScreen';
import WsInvoiceDetailScreen from './pages/well-servicing/invoices/InvoiceDetailScreen';
import WsAiDraftsScreen from './pages/well-servicing/ai/AiDraftsScreen';
import WsAiDraftDetailScreen from './pages/well-servicing/ai/AiDraftDetailScreen';
import WsMyToursScreen from './pages/well-servicing/field/WsMyToursScreen';
import WsFieldTourScreen, { WsFieldTourDayScreen } from './pages/well-servicing/field/WsFieldTourScreen';
import { WsFieldTourTestNewScreen } from './pages/well-servicing/field/WsFieldTourBop';
import WellServicingSettingsPage from './pages/settings/WellServicingSettingsPage';
// Wireline & Slickline WL-1: overview, wells (the shared register), units + crews, lines + events, tool strings, settings.
import WlOverviewScreen from './pages/wireline-slickline/OverviewScreen';
import WlWellsScreen from './pages/wireline-slickline/WellsScreen';
import WlNewWellScreen from './pages/wireline-slickline/NewWellScreen';
import WlWellDetailScreen from './pages/wireline-slickline/WellDetailScreen';
import WlUnitsScreen from './pages/wireline-slickline/UnitsScreen';
import WlNewUnitScreen from './pages/wireline-slickline/NewUnitScreen';
import WlUnitDetailScreen from './pages/wireline-slickline/UnitDetailScreen';
import WlLinesScreen from './pages/wireline-slickline/LinesScreen';
import WlNewLineScreen from './pages/wireline-slickline/NewLineScreen';
import WlLineDetailScreen from './pages/wireline-slickline/LineDetailScreen';
import WlToolStringsScreen from './pages/wireline-slickline/ToolStringsScreen';
import WlNewToolStringScreen from './pages/wireline-slickline/NewToolStringScreen';
import WlToolStringDetailScreen from './pages/wireline-slickline/ToolStringDetailScreen';
import WlJobsScreen from './pages/wireline-slickline/JobsScreen';
import WlNewJobScreen from './pages/wireline-slickline/NewJobScreen';
import WlJobDetailScreen from './pages/wireline-slickline/JobDetailScreen';
import WlSheetScreen from './pages/wireline-slickline/SheetScreen';
import WlRunScreen, { WlNewRunScreen } from './pages/wireline-slickline/RunScreen';
// WL-3 (fieldiq-api#1339): the end-of-job service report register and record page.
import WlReportsScreen from './pages/wireline-slickline/ReportsScreen';
import WlReportDetailScreen from './pages/wireline-slickline/ReportDetailScreen';
import WlRatesScreen from './pages/wireline-slickline/tickets/RatesScreen';
import WlRateDetailScreen, { WlNewRateScreen } from './pages/wireline-slickline/tickets/RateDetailScreen';
import WlTicketsScreen from './pages/wireline-slickline/tickets/TicketsScreen';
import WlReadyToInvoiceScreen from './pages/wireline-slickline/invoices/ReadyToInvoiceScreen';
import WlNewInvoiceScreen from './pages/wireline-slickline/invoices/NewInvoiceScreen';
import WlInvoiceDetailScreen from './pages/wireline-slickline/invoices/InvoiceDetailScreen';
import WlTicketDetailScreen from './pages/wireline-slickline/tickets/TicketDetailScreen';
import WlAiDraftsScreen from './pages/wireline-slickline/ai/AiDraftsScreen';
import WlAiDraftDetailScreen from './pages/wireline-slickline/ai/AiDraftDetailScreen';
import WlMySheetsScreen, {
  WlFieldNewRunScreen,
  WlFieldRunScreen,
  WlFieldSheetDayScreen,
  WlFieldSheetScreen,
  WlFieldTestNewScreen,
} from './pages/wireline-slickline/field/WlFieldScreens';
import WirelineSlicklineSettingsPage from './pages/settings/WirelineSlicklineSettingsPage';
import DownholeSettingsPage from './pages/settings/DownholeSettingsPage';
// The downhole field hand's shell (field_worker), inside the casing field tree.
import DownholeFieldLayout from './pages/downhole/field/DownholeFieldLayout';
import DownholeFieldAssembliesScreen from './pages/downhole/field/FieldAssembliesScreen';
import DownholeFieldAssemblyScreen from './pages/downhole/field/FieldAssemblyScreen';
import DownholeFieldTeardownScreen from './pages/downhole/field/FieldTeardownScreen';
import DownholeFieldWorkScreen from './pages/downhole/field/FieldWorkScreen';
import DownholeFieldWorkOrderScreen from './pages/downhole/field/FieldWorkOrderScreen';

// Safety Custom Forms
import SafetyFormTemplatesScreen from './pages/safety/SafetyFormTemplatesScreen';
import FormReplicatorUploadScreen from './pages/safety/FormReplicatorUploadScreen';
import FormExtractionReviewScreen from './pages/safety/FormExtractionReviewScreen';
import SafetyFormBuilderScreen from './pages/safety/SafetyFormBuilderScreen';
import DynamicSafetyFormScreen from './pages/safety/DynamicSafetyFormScreen';
import SafetySubmissionDetailScreen from './pages/safety/SafetySubmissionDetailScreen';
import ComplianceDashboard from './pages/safety/ComplianceDashboard';
import SafetyMetricsScreen from './pages/safety/SafetyMetricsScreen';
import CourseManagement from './pages/safety/CourseManagement';
import CourseEditScreen from './pages/safety/CourseEditScreen';
import MyTrainingScreen from './pages/safety/MyTrainingScreen';
import TakeTrainingScreen from './pages/safety/TakeTrainingScreen';

// Unified Field App
import MyWorkLayout from './pages/my-work/MyWorkLayout';
import FieldHomePage from './pages/my-work/FieldHomePage';
import TodayPage from './pages/my-work/TodayPage';
import SchedulePage from './pages/my-work/SchedulePage';
import FormsPage from './pages/my-work/FormsPage';
import HistoryPage from './pages/my-work/HistoryPage';
import ProfilePage from './pages/my-work/ProfilePage';

// Safety Forms
import PreJobMeetingForm from './forms/PreJobMeetingForm';
import JSAForm, { type JsaSaveIntent } from './forms/JSAForm';
import JsaEditPage from './pages/safety/JsaEditPage';
import { transformJsaData } from './lib/jsa';
import InspectionFormPage from './pages/safety/InspectionFormPage';
import IncidentReportForm from './forms/IncidentReportForm';
import InspectionDetailPage from './pages/safety/InspectionDetailPage';
import FieldInspectionsScreen from './screens/field/FieldInspectionsScreen';
import FieldTripInspectionsScreen from './screens/field/FieldTripInspectionsScreen';
import TripInspectionsScreen from './pages/trucking/TripInspectionsScreen';
import TripInspectionNewPage from './pages/trucking/TripInspectionNewPage';
import TripInspectionDetailPage from './pages/trucking/TripInspectionDetailPage';
import FieldInspectionResumeScreen from './screens/field/FieldInspectionResumeScreen';

// Components
import BottomNav from './components/BottomNav';
import AppShell from './components/AppShell';
import GlobalSearchModal from './components/GlobalSearchModal';
import FieldAgentPage from './components/field/FieldAgentPage';
import { fieldSearchGroups, recordAudience } from './lib/record-paths';
import { useSearchStore } from './stores/searchStore';
import { api } from './api/client';
import { invalidateSafety } from './lib/safety-invalidation';
import { REPORTABLE_INCIDENT_TYPES, MOTOR_VEHICLE_TYPE } from './lib/incident-types';
import { workItemBodyFields } from './lib/work-item-selection';
import { Loader2, CheckCircle, Mail, MessageSquare, Download, X } from 'lucide-react';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error: any) => {
        const status = error?.response?.status ?? error?.status;
        if (typeof status === 'number' && status >= 400 && status < 500) return false;
        return failureCount < 1;
      },
      // OPEN QUESTION — is `false` still what we want here?
      //
      // It is why the stale time-off banner survived five months instead of
      // five minutes. A second tab left open on Home never refetches on its
      // own: `staleTime` only decides whether a refetch is needed, not when one
      // is attempted, and with focus refetching off the only trigger left is a
      // remount. A tab nobody navigates in never remounts.
      //
      // Explicit invalidation now covers the time-off views (see
      // `invalidateTimeOff` in TimeOffScreen), but invalidation only reaches the
      // tab that performed the mutation — it cannot help a second tab, and it
      // cannot help when a DIFFERENT USER approves. For those, focus refetching
      // is the only thing that would correct the screen.
      //
      // Not flipped here on purpose: this is the global default for every query
      // in the app, and turning it on would change the request pattern of every
      // screen at once. If we want it, the cheap version is per-query
      // (`refetchOnWindowFocus: true` on the handful of dashboard counters that
      // go stale silently) rather than flipping the default.
      refetchOnWindowFocus: false,
      staleTime: 30_000,
    },
    mutations: {
      retry: 0,
    },
  },
});

/* ── Safety form wrappers that save to backend ── */

// Transform form data to match backend field names
function transformMeetingData(data: any) {
  return {
    meetingDate: data.date,
    startTime: data.date && data.time ? `${data.date}T${data.time}:00` : undefined,
    location: data.location,
    meetingType: 'pre_job',
    status: 'completed',
    topicsCovered: data.topics
      ? Object.entries(data.topics).filter(([, v]) => v).map(([k]) => k)
      : [],
    weatherConditions: data.weather?.condition || null,
    temperature: data.weather?.temp || null,
    siteHazardsNoted: data.hazards ? [data.hazards] : [],
    notes: data.controls || null,
    supervisorSignature: data.supervisorSignature || null,
  };
}

/**
 * Blank means "not supplied", never zero.
 *
 * `Number('')` is 0, so a `|| 0` or a bare `Number()` here would write a
 * measured zero into `days_away` or `vehicle_repair_cost` every time somebody
 * left the box alone. `undefined` is dropped by JSON.stringify and the column
 * keeps its NULL.
 */
function numberOrOmit(value: unknown): number | undefined {
  if (value === undefined || value === null || String(value).trim() === '') return undefined;
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
}

/** Blank string → omitted. Same reasoning as numberOrOmit, for dates and enums. */
function textOrOmit(value: unknown): string | undefined {
  const s = value === undefined || value === null ? '' : String(value).trim();
  return s === '' ? undefined : s;
}

function transformIncidentData(data: any) {
  /* Vehicle fields are sent ONLY for a motor vehicle incident. Someone who
   * picks Motor vehicle, fills the repair cost, then changes the type would
   * otherwise file a slip-and-fall carrying a vehicle and a repair bill. */
  const isMotorVehicle = data.type === MOTOR_VEHICLE_TYPE;

  return {
    incidentDate: data.date,
    incidentTime: data.date && data.time ? `${data.date}T${data.time}:00` : undefined,
    /* The form holds the canonical code ('minor_injury'), not a display
     * string, so there is nothing to lower-case-and-underscore here. Do not
     * reintroduce that: deriving a type from a label is how a label became a
     * type it was never meant to be. The API rejects anything outside the
     * reportable set anyway. */
    incidentType: data.type || REPORTABLE_INCIDENT_TYPES[0],
    severity: (data.severity || 'low').toLowerCase(),
    description: data.description,
    location: data.location,
    immediateActions: data.immediateActions,
    rootCause: data.rootCause,
    preventiveActions: data.preventiveActions,
    peopleInvolved: (data.people || []).filter((p: any) => p.selected).map((p: any) => ({
      name: p.name,
      role: p.role,
    })),

    /* ── Classification (0312) ──
     * camelCase, and that is not a slip. `/safety/incidents` reads camelCase on
     * the write path for these fields — see the note in CLAUDE.md. Sending
     * snake_case here is silently ignored by sanitizeUpdate and the incident
     * files unclassified.
     *
     * Omitted rather than defaulted when unset: an unclassified incident is a
     * real state that keeps the row in the review backlog, and a NULL
     * recordable_class counts toward no rate. Defaulting to 'not_recordable'
     * would let a backlog nobody has looked at read as a clean safety record. */
    recordableClass: textOrOmit(data.recordableClass),
    daysAway: numberOrOmit(data.daysAway),
    daysRestricted: numberOrOmit(data.daysRestricted),
    returnToWorkDate: textOrOmit(data.returnToWorkDate),

    /* `?? undefined`, never `|| undefined`: `false` is a reviewed verdict of
     * "not preventable" and has to survive. `||` would turn it back into "never
     * reviewed" and drop the incident out of the review queue entirely. */
    isPreventable: isMotorVehicle ? (data.isPreventable ?? undefined) : undefined,
    equipmentId: isMotorVehicle ? textOrOmit(data.equipmentId) : undefined,
    vehicleRepairCost: isMotorVehicle ? numberOrOmit(data.vehicleRepairCost) : undefined,
    /* A PT job picked on the incident form, as a work item (api#976). A casing
     * pick adds nothing — this transform has never sent the casing job, and
     * that is left exactly as it was here. */
    ...workItemBodyFields(data.job),
  };
}

/* ── Share modal for entering email / phone ── */
function ShareModal({ type, onClose, onSend }: {
  type: 'email' | 'sms';
  onClose: () => void;
  onSend: (value: string) => void;
}) {
  const [value, setValue] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSend = async () => {
    if (!value.trim()) return;
    setSending(true);
    try {
      await onSend(value.trim());
      setSent(true);
      setTimeout(onClose, 1500);
    } catch {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 animate-fade-in">
      <div className="bg-fieldiq-card border border-fieldiq-border rounded-2xl p-5 w-80 shadow-xl">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-white font-semibold">{type === 'email' ? 'Email Report' : 'Text Report'}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><X size={18} /></button>
        </div>
        {sent ? (
          <div className="text-center py-4">
            <CheckCircle size={36} className="text-green-400 mx-auto mb-2" />
            <p className="text-green-400 font-medium">Sent!</p>
          </div>
        ) : (
          <>
            <input
              type={type === 'email' ? 'email' : 'tel'}
              placeholder={type === 'email' ? 'email@example.com' : '+1 (555) 123-4567'}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-white text-sm mb-3 focus:outline-none focus:border-fieldiq-accent"
              autoFocus
            />
            <button
              onClick={handleSend}
              disabled={!value.trim() || sending}
              className="w-full bg-fieldiq-accent text-white font-semibold py-2.5 rounded-lg disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {sending ? <Loader2 size={16} className="animate-spin" /> : type === 'email' ? <Mail size={16} /> : <MessageSquare size={16} />}
              {sending ? 'Sending...' : 'Send'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

/* ── Map form type → API methods ── */
type SafetyFormType = 'meeting' | 'jsa' | 'incident';

function getShareActions(formType: SafetyFormType) {
  switch (formType) {
    case 'meeting':
      return {
        email: (id: string, data: any) => api.safety.emailMeeting(id, data),
        sms: (id: string, data: any) => api.safety.smsMeeting(id, data),
        pdf: (id: string) => api.safety.getMeetingPdf(id),
      };
    case 'jsa':
      return {
        email: (id: string, data: any) => api.safety.emailJsa(id, data),
        sms: (id: string, data: any) => api.safety.smsJsa(id, data),
        pdf: (id: string) => api.safety.getJsaPdf(id),
      };
    case 'incident':
      return {
        email: (id: string, data: any) => api.safety.emailIncident(id, data),
        sms: (id: string, data: any) => api.safety.smsIncident(id, data),
        pdf: (id: string) => api.safety.getIncidentPdf(id),
      };
  }
}

function SafetyFormPage({ title, Form, apiCall, transform, nav, formType, submitAfterCreate }: {
  title: string;
  Form: React.ComponentType<{ onSubmit: (data: any, intent?: JsaSaveIntent) => void; onCancel: () => void; saving?: boolean }>;
  apiCall: (data: any) => Promise<any>;
  transform?: (data: any) => any;
  nav?: React.ReactNode;
  formType: SafetyFormType;
  /**
   * For a form whose create leaves a draft (the JSA): the call that sends it
   * for approval, run after the create when the reader chose Submit. Without
   * it, "Submit for Approval" only ever created a draft.
   */
  submitAfterCreate?: (id: string) => Promise<unknown>;
}) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState('');
  const [savedId, setSavedId] = useState<string | null>(null);
  const [shareModal, setShareModal] = useState<'email' | 'sms' | null>(null);
  const [downloading, setDownloading] = useState(false);
  // What the save actually did, for the success screen. Only a form with
  // `submitAfterCreate` can end anywhere but 'saved'.
  const [outcome, setOutcome] = useState<{ kind: 'saved' | 'draft' | 'submitted' | 'submit-failed'; detail?: string }>({ kind: 'saved' });

  const handleSubmit = async (rawData: any, intent?: JsaSaveIntent) => {
    setSaving(true);
    try {
      const data = transform ? transform(rawData) : rawData;
      const res = await apiCall(data);
      invalidateSafety(qc);
      const id = res?.data?.data?.id || res?.data?.id;
      if (submitAfterCreate) {
        if (intent === 'submit' && id) {
          try {
            await submitAfterCreate(id);
            invalidateSafety(qc);
            setOutcome({ kind: 'submitted' });
          } catch (submitErr: any) {
            // The create landed and the submit did not. Said as both halves:
            // "saved" alone would read as sent for approval.
            setOutcome({ kind: 'submit-failed', detail: submitErr?.response?.data?.error || 'the server did not accept it' });
          }
        } else {
          setOutcome({ kind: 'draft' });
        }
      }
      setSavedId(id || 'saved');
    } catch (err: any) {
      const msg = err.response?.data?.error || 'Failed to save';
      console.error('Safety form save error:', err.response?.data || err);
      setToast(msg);
      setSaving(false);
      setTimeout(() => setToast(''), 4000);
    }
  };

  const actions = getShareActions(formType);

  const handleDownloadPdf = async () => {
    if (!savedId || savedId === 'saved') return;
    setDownloading(true);
    try {
      const res = await actions.pdf(savedId);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${formType}-${savedId.slice(0, 8)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('PDF download error:', err);
      setToast('Failed to download PDF');
      setTimeout(() => setToast(''), 3000);
    } finally {
      setDownloading(false);
    }
  };

  // Success screen after save
  if (savedId) {
    return (
      <div className="animate-fade-in">
        <header className="z-40 bg-fieldiq-dark/95 backdrop-blur border-b border-fieldiq-border px-4 py-3 flex items-center gap-3" style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 0.75rem)' }}>
          <h1 className="text-lg font-bold">{title}</h1>
        </header>
        <div className="flex flex-col items-center justify-center px-6 py-12">
          <CheckCircle size={56} className="text-green-400 mb-4" />
          <h2 className="text-xl font-bold text-white mb-1">
            {outcome.kind === 'submitted' ? 'Submitted for Approval'
              : outcome.kind === 'draft' || outcome.kind === 'submit-failed' ? 'Saved as a Draft'
              : 'Saved Successfully'}
          </h2>
          <p className="text-gray-400 text-sm mb-8" data-testid="safety-form-outcome">
            {outcome.kind === 'submitted' ? `Your ${title.toLowerCase()} has been sent for approval.`
              : outcome.kind === 'draft' ? `Your ${title.toLowerCase()} is saved as a draft. It has not been sent for approval.`
              : outcome.kind === 'submit-failed' ? `It was saved as a draft, but submitting it for approval failed: ${outcome.detail}. Submit it from Safety records.`
              : `Your ${title.toLowerCase()} has been recorded.`}
          </p>

          <div className="w-full max-w-xs space-y-3">
            {savedId !== 'saved' && (
              <>
                <button
                  onClick={() => setShareModal('email')}
                  className="w-full flex items-center gap-3 bg-fieldiq-card border border-fieldiq-border rounded-xl px-4 py-3 text-white hover:border-fieldiq-accent transition-colors"
                >
                  <Mail size={20} className="text-fieldiq-accent" />
                  <span className="font-medium">Email Report</span>
                </button>
                <button
                  onClick={() => setShareModal('sms')}
                  className="w-full flex items-center gap-3 bg-fieldiq-card border border-fieldiq-border rounded-xl px-4 py-3 text-white hover:border-fieldiq-accent transition-colors"
                >
                  <MessageSquare size={20} className="text-fieldiq-accent" />
                  <span className="font-medium">Text (SMS) Summary</span>
                </button>
                <button
                  onClick={handleDownloadPdf}
                  disabled={downloading}
                  className="w-full flex items-center gap-3 bg-fieldiq-card border border-fieldiq-border rounded-xl px-4 py-3 text-white hover:border-fieldiq-accent transition-colors disabled:opacity-50"
                >
                  {downloading ? <Loader2 size={20} className="animate-spin text-fieldiq-accent" /> : <Download size={20} className="text-fieldiq-accent" />}
                  <span className="font-medium">{downloading ? 'Generating PDF...' : 'Download PDF'}</span>
                </button>
              </>
            )}
            <button
              onClick={() => navigate(-1)}
              className="w-full bg-fieldiq-accent text-white font-semibold py-3 rounded-xl mt-4"
            >
              Done
            </button>
          </div>
        </div>
        {nav}
        {shareModal && savedId !== 'saved' && (
          <ShareModal
            type={shareModal}
            onClose={() => setShareModal(null)}
            onSend={async (value) => {
              if (shareModal === 'email') {
                await actions.email(savedId, { to: value });
              } else {
                await actions.sms(savedId, { to: value });
              }
            }}
          />
        )}
        {toast && (
          <div className="fixed bottom-[var(--fab-bottom)] left-1/2 -translate-x-1/2 bg-fieldiq-card border border-fieldiq-border text-white px-4 py-2.5 rounded-xl shadow-lg text-sm animate-fade-in z-50">
            {toast}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      <header className="z-40 bg-fieldiq-dark/95 backdrop-blur border-b border-fieldiq-border px-4 py-3 flex items-center gap-3" style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 0.75rem)' }}>
        <button onClick={() => navigate(-1)} className="text-fieldiq-text">&larr; Back</button>
        <h1 className="text-lg font-bold">{title}</h1>
        {saving && <Loader2 size={18} className="animate-spin text-fieldiq-accent ml-auto" />}
      </header>
      <div className="px-4 py-4">
        <Form onSubmit={handleSubmit} onCancel={() => navigate(-1)} saving={saving} />
      </div>
      {nav}
      {toast && (
        <div className="fixed bottom-[var(--fab-bottom)] left-1/2 -translate-x-1/2 bg-fieldiq-card border border-fieldiq-border text-white px-4 py-2.5 rounded-xl shadow-lg text-sm animate-fade-in z-50">
          {toast}
        </div>
      )}
    </div>
  );
}

/**
 * The field tree scrolls the document (`.field-shell` has no box of its own),
 * so its Back restoration is aimed at the window. The office tree's lives in
 * AppLayout, aimed at <main>.
 */
function FieldScrollRestoration() {
  useScrollRestoration(() => window);
  return null;
}

/**
 * `vertical` is one key, or a list where ANY one opens the route — independent
 * positive checks, never a negation (#815: fleet, drivers and trip inspections
 * serve trucking and surface rentals tenants alike, api#1041 / api#1043).
 */
function VerticalGuard({ vertical, children }: { vertical: string | readonly string[]; children: React.ReactNode }) {
  const { hasVertical } = useAuth();
  const keys = typeof vertical === 'string' ? [vertical] : vertical;
  if (!keys.some((k) => hasVertical(k))) return <Navigate to="/" replace />;
  return <>{children}</>;
}

/** The trucking surfaces a surface rentals tenant also runs: its trucks, drivers and trip inspections. */
const HAULING_VERTICALS = ['trucking', 'surface_rentals'] as const;

/*
 * The casing-only screens of the /field tree (My Jobs, My Tickets, My
 * Inspections). Their tiles were already guarded, but the routes were not, so a
 * pressure-testing or downhole tech could still reach them by URL and get a
 * casing 403 rendered as raw text. Unlike VerticalGuard this redirects ONLY once
 * the company is known: a casing hand whose profile has not loaded (an offline
 * boot) keeps reaching these screens exactly as before.
 */
function CasingFieldGuard({ children }: { children: React.ReactNode }) {
  const { company, hasVertical } = useAuth();
  if (company && !hasVertical('casing_running')) return <Navigate to="/field" replace />;
  return <>{children}</>;
}

function GlobalSearch() {
  const location = useLocation();
  const open = useSearchStore((s) => s.open);
  const close = useSearchStore((s) => s.close);
  const onLogin = location.pathname.startsWith('/login');
  // A field hand with nothing searchable gets no modal and no shortcut, the same
  // rule that hides the header's Search button (lib/record-paths.ts).
  const { isFieldWorker, hasVertical } = useAuth();
  const searchable = !isFieldWorker || fieldSearchGroups(recordAudience(true, hasVertical), hasVertical).length > 0;

  useEffect(() => {
    if (onLogin || !searchable) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        open();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onLogin, searchable, open]);

  // Close the modal on route changes so stale results don't linger.
  useEffect(() => {
    close();
  }, [location.pathname, close]);

  if (onLogin || !searchable) return null;
  return <GlobalSearchModal />;
}


/**
 * Where `/` lands: the one widget Overview, for every vertical (api#873).
 *
 * Until the Overview a trucking tenant was redirected to /trucking and a
 * rentals-only tenant to /rentals. Those pages and their routes are still here
 * and still in the sidebar; `/` simply no longer sends anyone to them. The
 * widgets a company sees are the API's answer for its verticals, so there is no
 * vertical branch left to take here.
 */
function HomeRedirect() {
  return <OverviewScreen />;
}

/**
 * /trucking/loads/:loadId/bol was a second screen for one BOL: the same GET
 * and the same <BolDocument /> as /trucking/bols/:id, with a different set of
 * actions. A load IS its BOL (there is no bills_of_lading table), so the id is
 * the same id and the office route now lands on the one BOL detail page. The
 * component itself stays, for a driver's /driver/bol/:loadId.
 */
function LoadBolRedirect() {
  const { loadId } = useParams();
  return <Navigate to={`/trucking/bols/${loadId}`} replace />;
}

/** Signed out: to /login, carrying the requested page as ?next= (lib/post-login-target). */
function LoginRedirect() {
  const location = useLocation();
  return <Navigate to={loginPathFor(location)} replace />;
}

/**
 * Signed in, still on /login: go to the ?next= target, else this tree's home.
 *
 * LoginScreen navigates to the same target itself; this route exists because
 * the tree swaps to the signed-in one the moment the session lands, and
 * without a /login route here its catch-all would race that navigation and
 * could win with the home screen.
 */
function PostLoginRedirect({ fallback }: { fallback: string }) {
  const location = useLocation();
  return <Navigate to={nextFromSearch(location.search) ?? fallback} replace />;
}

function FieldJobLinkRedirect() {
  const { jobId } = useParams();
  return <Navigate to={`/jobs/${jobId}`} replace />;
}

function AppRoutes() {
  const { isAuthenticated, isLoading } = useAuth();

  // Public route — ticket review via magic link (no auth required).
  // Every public token page here must also be in lib/public-routes.ts, or a
  // stale staff session in the browser can bounce its reader to /login.
  const location = window.location.pathname;
  if (location.startsWith('/ticket-review/')) {
    return (
      <Routes>
        <Route path="/ticket-review/:token" element={<TicketReviewPage />} />
      </Routes>
    );
  }

  // The Earthworks customer's emailed quote link (EW-2): accept or decline,
  // no login.
  if (location.startsWith('/quote-review/')) {
    return (
      <Routes>
        <Route path="/quote-review/:token" element={<QuoteReviewPage />} />
        <Route path="/quote-review/change-orders/:token" element={<ChangeOrderReviewPage />} />
      </Routes>
    );
  }

  // The Earthworks invoice "Pay now" link (EW-6b): read the invoice, pay by
  // card when the tenant's Stripe is connected. No login.
  if (location.startsWith('/invoice-pay/')) {
    return (
      <Routes>
        <Route path="/invoice-pay/:token" element={<InvoicePayPage />} />
      </Routes>
    );
  }

  // The Earthworks change-order link as the API's email writes it (EW-5,
  // ewCoReviewUrl): the same page as /quote-review/change-orders/:token.
  if (location.startsWith('/change-order-review/')) {
    return (
      <Routes>
        <Route path="/change-order-review/:token" element={<ChangeOrderReviewPage />} />
      </Routes>
    );
  }

  // The rental customer's emailed link. Must return before the signed-in
  // customer portal's /portal/* routes, which would send this reader to /login.
  if (location.startsWith('/portal/rentals/')) {
    return (
      <Routes>
        <Route path="/portal/rentals/:token" element={<RentalPortalPage />} />
      </Routes>
    );
  }

  // Invite setup-password is always standalone — never wrap it in an
  // authenticated layout. An already-logged-in visitor following an invite
  // link (e.g. being added to a second tenant) would otherwise see the
  // current tenant's name in the wrapping layout's header, which leaks the
  // wrong company name onto a different tenant's invite page.
  if (location === '/invite/setup-password') {
    return (
      <Routes>
        <Route path="/invite/setup-password" element={<InviteSetupScreen />} />
      </Routes>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-fieldiq-dark flex items-center justify-center">
        <div className="text-fieldiq-accent">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/login" element={<LoginScreen />} />
        <Route path="/forgot-password" element={<ForgotPasswordScreen />} />
        <Route path="/reset-password" element={<ResetPasswordScreen />} />
        <Route path="/invite/setup-password" element={<InviteSetupScreen />} />
        {/* Carries the page the reader asked for as ?next=, so a link opened
            signed out (the ticket-reminder SMS) lands on it after sign-in. */}
        <Route path="*" element={<LoginRedirect />} />
      </Routes>
    );
  }

  // The embedded super-admin route tree lived here. It has been removed along
  // with the /super-admin API it called: that console is superseded by the
  // Platform Admin Hub (admin.field-iq.ai, the fieldiq-admin-hub repo), which
  // authenticates against platform_users rather than a tenant role.
  //
  // It was already unreachable. `isSuperAdmin` came only from /auth/me
  // returning role === 'super_admin', and no user in production has that role;
  // api.auth.superAdminLogin had no call site anywhere in this app.
  //
  // One route was collateral rather than dead by design: /super-admin/help-videos
  // was the only admin UI for help videos. That capability was ALREADY broken —
  // it called /admin/help-videos against the /api/v1 base, resolving to a path
  // with no handler, while the live endpoint is /api/admin/help-videos. Removing
  // this tree makes the gap visible instead of causing it. HelpVideosAdmin.tsx
  // and api.helpVideos.admin are deliberately KEPT for the hub page that will
  // rehome them.

  // Everything below is signed in, and every date in it is computed in the
  // tenant's own zone — so none of it renders until that zone is known
  // (TenantZoneGate, lib/tenant-zone.ts). The public routes above are not
  // gated: they belong to no signed-in tenant.
  return (
    <TenantZoneGate>
      <AuthenticatedRoutes />
    </TenantZoneGate>
  );
}

function AuthenticatedRoutes() {
  const { isFieldWorker, isCustomer, hasVertical } = useAuth();
  const globalChat = useGlobalChat();

  if (isCustomer) {
    return (
      <Routes>
        <Route path="/portal" element={<PortalDashboard />} />
        <Route path="/portal/invoices" element={<PortalInvoices />} />
        <Route path="/portal/invoices/:id" element={<PortalInvoiceDetail />} />
        <Route path="/portal/quotes" element={<PortalQuotes />} />
        <Route path="/portal/quotes/:id" element={<PortalQuoteDetail />} />
        <Route path="/portal/profile" element={<PortalProfile />} />
        <Route path="/login" element={<PostLoginRedirect fallback="/portal" />} />
        <Route path="*" element={<Navigate to="/portal" replace />} />
      </Routes>
    );
  }

  if (isFieldWorker) {
    const isTruckingDriver = hasVertical('trucking');
    const isRentalTech = hasVertical('surface_rentals');

    // Unified field worker app for trucking drivers & rental techs.
    // `.field-shell`, as on the casing tree below: no bottom bar is drawn here
    // any more (fix/field-home-trucking-rentals), so its screens stop
    // reserving --bottom-chrome for one.
    if (isTruckingDriver || isRentalTech) {
      return (
        <div className="field-shell">
        {/* The offline surface, as on the casing tree below (fe#815): the
            sync bar sits above every screen's header. */}
        <SyncStatusBar />
        <Routes>
          {/* ── Unified /my-work shell ── */}
          {/* /my-work IS the home (fix/field-home-trucking-rentals): the clock card
            * and the tiles, casing's shape. The bottom bars are gone; every page
            * below has a back arrow to it. */}
          <Route path="/my-work" element={<MyWorkLayout />}>
            <Route index element={<FieldHomePage />} />
            <Route path="today" element={<TodayPage />} />
            <Route path="schedule" element={<SchedulePage />} />
            <Route path="forms" element={<FormsPage />} />
            <Route path="history" element={<HistoryPage />} />
            <Route path="profile" element={<ProfilePage />} />
          </Route>

          {/* ── Preserved driver detail screens ── */}
          <Route path="/driver/load/:loadId" element={<DriverLoadView />} />
          <Route path="/driver/bols" element={<DriverBOLsListScreen />} />
          <Route path="/driver/bols/:bolId" element={<DriverBOLScreen />} />
          {/* The master BOL's operator signature from the phone (api#941). Way in: the rig-move banner on DriverLoadView. */}
          <Route path="/driver/move-jobs/:moveJobId/master-bol" element={<DriverMasterBolScreen />} />
          <Route path="/driver/weight-ticket/new" element={<DriverWeightTicketScreen />} />
          <Route path="/driver/weight-ticket/:loadId" element={<DriverWeightTicketScreen />} />
          <Route path="/driver/bol/:loadId" element={<BolDocumentView />} />
          <Route path="/driver/profile" element={<DriverProfileScreen />} />

          {/* Eight routes were removed from THIS tree in the wiring pass, all of
            * them unreachable duplicates of a route sitting a few lines below in
            * the same <Routes>:
            *   /driver/pay, /driver/time-off      → /field/pay, /field/time-off
            *   /driver/safety                     → /field/safety (a superset:
            *                                        DriverSafetyScreen omitted
            *                                        incidents and meetings)
            *   /driver/safety/{jsa,inspection,incident}
            *                                      → the identical /field/safety/*
            *                                        elements below
            *   /field/rentals/{tickets,inspections}
            *                                      → the office desktop pages,
            *                                        mounted in the field tree
            * The office tree's copies of these /driver/* paths were deleted later,
            * along with DriverSafetyScreen, DriverTimeOffScreen, DriverHomeScreen
            * and DriverLoadScreen: nothing in the office app linked to them. */}

          {/* ── Preserved rental field tech detail screens ── */}
          <Route path="/field/rentals/delivery/:dispatchId" element={<DeliveryConfirmation />} />
          <Route path="/field/rentals/return/:dispatchId" element={<ReturnConditionCheck />} />
          {/* The tech's Yard check-out/in and Inspections tiles open the office
            * screens, at the office paths so their own links between list, new
            * and detail keep working. The API already serves them to a
            * field_worker: the reads are open to the tenant, filing an
            * inspection is RENTAL_INSPECTION_WRITE (field_worker included), and
            * check-out / check-in carry no role gate. Positive guard: a trucking
            * driver without rentals is sent home. `surface="field"` swaps the
            * office band and stat strip for the casing field header (fe#815);
            * the office tree's elements pass nothing and are unchanged. */}
          <Route path="/rentals/yard" element={<VerticalGuard vertical="surface_rentals"><RentalYard surface="field" /></VerticalGuard>} />
          <Route path="/rentals/inspections" element={<VerticalGuard vertical="surface_rentals"><RentalInspections surface="field" /></VerticalGuard>} />
          <Route path="/rentals/inspections/new" element={<VerticalGuard vertical="surface_rentals"><RentalInspectionDraft surface="field" /></VerticalGuard>} />
          <Route path="/rentals/inspections/:id" element={<VerticalGuard vertical="surface_rentals"><RentalInspectionDetail surface="field" /></VerticalGuard>} />

          {/* ── Shared safety & profile screens ── */}
          <Route path="/field/profile" element={<FieldProfileScreen />} />
          <Route path="/field/pay" element={<FieldPayScreen />} />
          <Route path="/field/safety" element={<FieldSafetyScreen />} />
          <Route path="/field/safety/jsa/:id/edit" element={<JsaEditPage />} />
          <Route path="/field/safety/jsa" element={
            <SafetyFormPage title="Job Safety Analysis" Form={JSAForm} apiCall={(d) => api.safety.createJsa(d)} transform={transformJsaData} formType="jsa" submitAfterCreate={(id) => api.safety.submitJsa(id)} />
          } />
          <Route path="/field/safety/inspection" element={
            <InspectionFormPage />
          } />
          {/* A driver's own trip inspections (api#855). Only this tree: a
              trucking tenant's field logins land here, never in the casing
              field tree. Specific before `:id`. */}
          <Route path="/field/trip-inspections" element={<FieldTripInspectionsScreen />} />
          <Route path="/field/trip-inspections/new" element={<TripInspectionNewPage />} />
          <Route path="/field/trip-inspections/:id" element={<TripInspectionDetailPage />} />
          <Route path="/field/safety/incident" element={
            <SafetyFormPage title="Report Incident" Form={IncidentReportForm} apiCall={(d) => api.safety.createIncident(d)} transform={transformIncidentData} formType="incident" />
          } />
          <Route path="/field/time-off" element={<FieldTimeOffScreen />} />
          <Route path="/field/clock-in" element={<ClockInScreen />} />
          <Route path="/field/clock-in/shift/:timesheetId/edit" element={<OwnShiftEditScreen />} />
          <Route path="/field/clock-in/missed-shift" element={<MissedShiftScreen />} />
          <Route path="/forms/dynamic/:templateId" element={<DynamicSafetyFormScreen />} />
          <Route path="/forms/pre-job-meeting" element={<PreJobMeetingForm />} />

          {/* ── Compliance training (shared /safety paths) ── */}
          <Route path="/safety/my-training" element={<MyTrainingScreen />} />
          <Route path="/safety/training/:courseId/take" element={<TakeTrainingScreen />} />

          {/* ── Notification preferences ── */}
          <Route path="/field/settings/notifications" element={<NotificationPreferencesScreen />} />
          {/* The header's AI button (fe#815). Only the office tree routed /agent,
              so here it fell through to the catch-all and did nothing. */}
          <Route path="/agent" element={<FieldAgentPage />} />

          {/* ── Redirects from old routes ── */}
          <Route path="/driver" element={<Navigate to="/my-work" replace />} />
          <Route path="/field/rentals" element={<Navigate to="/my-work" replace />} />

          <Route path="/login" element={<PostLoginRedirect fallback="/my-work" />} />
          <Route path="*" element={<Navigate to="/my-work" replace />} />
        </Routes>
        {/* Search, for a field hand whose tree can open what it finds (fe#815). */}
        <GlobalSearch />
        </div>
      );
    }

    /* ── The casing field-worker shell ──────────────────────────────────────
     *
     * THERE IS NO BOTTOM TAB BAR IN THIS TREE. The home screen's tile list is
     * the whole of the navigation, and every screen below carries a back
     * control to /field. `<FieldBottomNav />` used to render here, as a sibling
     * of these routes.
     *
     * FieldBottomNav.tsx is gone too: DriverLoadView and BolDocumentView, in
     * the /my-work tree above, were its last two renders, and that tree has
     * one tile home now with no bottom bar (fix/field-home-trucking-rentals).
     *
     * `.field-shell` (index.css) exists only to re-declare --bottom-chrome and
     * --fab-bottom for this subtree, so the 14 screens here stop reserving
     * 68px for a nav that is gone. The :root values are untouched — the office
     * tree reads them and still has a nav. The rule also takes the wrapper's
     * own box out of the layout, so nothing below it sees a new ancestor.
     */
    return (
      <div className="field-shell">
        {/* The offline surface, on the tree that goes offline.
         *
         * It rendered ONLY on the office HomeScreen, so the 14 screens a field
         * hand actually uses had no report of connectivity, of how much work
         * was queued, or — since the offline-session fix — of a sign-in that
         * was restored from the device rather than confirmed by the server.
         * That is the audience the bar was written for.
         *
         * `.field-shell` takes its own box out of the layout (index.css), so
         * this is a plain in-flow sibling of the screen below, above each
         * screen's own <Header />, exactly as it sits on HomeScreen. It is NOT
         * inside the viewport-height, clipped wrapper AppLayout uses, so it
         * cannot reintroduce the document-level scroll that CLAUDE.md's
         * placement warning is about — measured 0px on /field/tickets.
         *
         * The words for those two utilities are deliberately not spelled here:
         * the stylesheet scanner reads comments as source, and both already
         * ship from real call sites, so naming them would mint nothing and a
         * rule-level diff would report clean. See CLAUDE.md. */}
        <SyncStatusBar />
        <FieldScrollRestoration />
        <Routes>
          <Route path="/field" element={<FieldHomeScreen />} />
          {/* Downhole tools for a field hand. A downhole tenant's field_worker
              lands in THIS tree (it is neither trucking nor rentals), so this
              is where the routes go. A positive vertical guard, never a
              negative one; the way in is the Downhole tile on /field and the
              row on More, both shown only to a downhole_tools tenant. Specific
              before `:id`. See src/pages/downhole/field/DownholeFieldLayout.tsx. */}
          <Route path="/field/downhole" element={<VerticalGuard vertical="downhole_tools"><DownholeFieldLayout /></VerticalGuard>}>
            <Route index element={<DownholeFieldAssembliesScreen />} />
            <Route path="assemblies/:id/teardown" element={<DownholeFieldTeardownScreen />} />
            <Route path="assemblies/:id" element={<DownholeFieldAssemblyScreen />} />
            <Route path="work/:id" element={<DownholeFieldWorkOrderScreen />} />
            <Route path="work" element={<DownholeFieldWorkScreen />} />
          </Route>
          {/* Pressure testing for a field hand (PT-3). A pressure_testing tenant's
              field_worker lands in THIS tree (neither trucking nor rentals). A
              positive guard on each route; the way in is the Pressure tests entry
              on /field and its row on More, both under the same guard. Specific
              before `:id`. The test screen carries its job as ?job= — there is
              no GET /tests/:id. See src/pages/pressure-testing/field/. */}
          <Route path="/field/pt" element={<VerticalGuard vertical="pressure_testing"><PtMyJobsScreen /></VerticalGuard>} />
          <Route path="/field/pt/jobs/:id" element={<VerticalGuard vertical="pressure_testing"><PtFieldJobScreen /></VerticalGuard>} />
          <Route path="/field/pt/tests/:id" element={<VerticalGuard vertical="pressure_testing"><PtFieldTestScreen /></VerticalGuard>} />
          {/* PT-5: a field ticket's review and the company man's signature; reached from the job screen. */}
          <Route path="/field/pt/tickets/:id" element={<VerticalGuard vertical="pressure_testing"><PtFieldTicketScreen /></VerticalGuard>} />
          {/* NDT for a field hand (fieldiq-api#1144). An ndt_inspection tenant's
              field_worker lands in THIS tree. Positive guard per route; the way in
              is the My NDT Jobs entry on /field and its row on More, both under
              the same guard. Specific before `:id`. */}
          <Route path="/field/ndt" element={<VerticalGuard vertical="ndt_inspection"><NdtMyJobsScreen /></VerticalGuard>} />
          <Route path="/field/ndt/jobs/:jobId/welds/:weldId" element={<VerticalGuard vertical="ndt_inspection"><NdtFieldWeldScreen /></VerticalGuard>} />
          <Route path="/field/ndt/jobs/:id" element={<VerticalGuard vertical="ndt_inspection"><NdtFieldJobScreen /></VerticalGuard>} />
          {/* NDT batch 4 (fieldiq-api#1157): a field ticket, reviewed and signed on the phone. */}
          <Route path="/field/ndt/tickets/:id" element={<VerticalGuard vertical="ndt_inspection"><NdtFieldTicketScreen /></VerticalGuard>} />
          {/* Pipeline & Facility for a field hand (fieldiq-api#1200, PF-2). The way in
              is the My PF Jobs entry on /field and its row on More, under the same guard. */}
          <Route path="/field/pf" element={<VerticalGuard vertical="pipeline_facility"><PfMyJobsScreen /></VerticalGuard>} />
          <Route path="/field/pf/jobs/:id/log/:workDate" element={<VerticalGuard vertical="pipeline_facility"><PfFieldLogScreen /></VerticalGuard>} />
          <Route path="/field/pf/jobs/:id" element={<VerticalGuard vertical="pipeline_facility"><PfFieldJobScreen /></VerticalGuard>} />
          {/* PF-3a (fieldiq-api#1219): the hand's digs and the dig sheet. */}
          <Route path="/field/pf/digs" element={<VerticalGuard vertical="pipeline_facility"><PfMyDigsScreen /></VerticalGuard>} />
          <Route path="/field/pf/digs/:id" element={<VerticalGuard vertical="pipeline_facility"><PfFieldDigScreen /></VerticalGuard>} />
          {/* Well Testing for a field hand (fieldiq-api#1245, WT-2). The way in is the
              My WT Jobs entry on /field and its row on More, under the same guard. */}
          <Route path="/field/wt" element={<VerticalGuard vertical="well_testing"><WtMyJobsScreen /></VerticalGuard>} />
          <Route path="/field/wt/jobs/:id/log/:workDate/:shift" element={<VerticalGuard vertical="well_testing"><WtFieldShiftLogScreen /></VerticalGuard>} />
          <Route path="/field/wt/jobs/:id/reading" element={<VerticalGuard vertical="well_testing"><WtFieldReadingScreen /></VerticalGuard>} />
          <Route path="/field/wt/jobs/:id" element={<VerticalGuard vertical="well_testing"><WtFieldJobScreen /></VerticalGuard>} />
          <Route path="/field/ws" element={<VerticalGuard vertical="well_servicing"><WsMyToursScreen /></VerticalGuard>} />
          <Route path="/field/ws/jobs/:id/tours/:workDate" element={<VerticalGuard vertical="well_servicing"><WsFieldTourDayScreen /></VerticalGuard>} />
          <Route path="/field/ws/tours/:id" element={<VerticalGuard vertical="well_servicing"><WsFieldTourScreen /></VerticalGuard>} />
          {/* fieldiq-api#1324: the rig manager or driller records a BOP test ON the sheet — the shared
              test form, posting to the sheet's field door. Reached from the sheet's Record BOP test. */}
          <Route path="/field/ws/tours/:id/pressure-tests/new" element={<VerticalGuard vertical="well_servicing"><WsFieldTourTestNewScreen /></VerticalGuard>} />
          <Route path="/field/wl" element={<VerticalGuard vertical="wireline_slickline"><WlMySheetsScreen /></VerticalGuard>} />
          <Route path="/field/wl/jobs/:id/sheets/:workDate" element={<VerticalGuard vertical="wireline_slickline"><WlFieldSheetDayScreen /></VerticalGuard>} />
          <Route path="/field/wl/sheets/:id" element={<VerticalGuard vertical="wireline_slickline"><WlFieldSheetScreen /></VerticalGuard>} />
          <Route path="/field/wl/sheets/:id/runs/new" element={<VerticalGuard vertical="wireline_slickline"><WlFieldNewRunScreen /></VerticalGuard>} />
          <Route path="/field/wl/sheets/:id/runs/:runId" element={<VerticalGuard vertical="wireline_slickline"><WlFieldRunScreen /></VerticalGuard>} />
          <Route path="/field/wl/sheets/:id/pressure-tests/new" element={<VerticalGuard vertical="wireline_slickline"><WlFieldTestNewScreen /></VerticalGuard>} />
          {/* The shared Pressure Control register (BOP-1/BOP-2) for a well_servicing hand.
              The API has let field_worker READ every pressure-control route since BOP-1
              (PC_READ_ROLES); this tree simply had no route, so the catch-all sent the
              hand to /field. Same paths as the office tree, because the screens link to
              each other by PC_LIST. READ ONLY: the screens draw no write control for
              field_worker (canAdminPc / canRecordPcTest). /tests/new IS registered, only
              so it is not read as a test id by /tests/:testId: for a hand it renders the
              screen's own "your role cannot record a pressure test" panel, because
              POST /tests is PC_EVENT (office + supervisor). The way in is the Pressure
              Control entry on /field and its row on More. */}
          <Route path="/equipment/pressure-control" element={<VerticalGuard vertical="well_servicing"><PressureControlScreen /></VerticalGuard>} />
          <Route path="/equipment/pressure-control/tests" element={<VerticalGuard vertical="well_servicing"><PressureControlTestsScreen /></VerticalGuard>} />
          <Route path="/equipment/pressure-control/tests/new" element={<VerticalGuard vertical="well_servicing"><PressureControlTestNewScreen /></VerticalGuard>} />
          <Route path="/equipment/pressure-control/tests/:testId" element={<VerticalGuard vertical="well_servicing"><PressureControlTestDetailScreen /></VerticalGuard>} />
          <Route path="/equipment/pressure-control/drills" element={<VerticalGuard vertical="well_servicing"><PressureControlDrillsScreen /></VerticalGuard>} />
          <Route path="/equipment/pressure-control/drills/:drillId" element={<VerticalGuard vertical="well_servicing"><PressureControlDrillDetailScreen /></VerticalGuard>} />
          <Route path="/equipment/pressure-control/calibrations" element={<VerticalGuard vertical="well_servicing"><PressureControlCalibrationsScreen /></VerticalGuard>} />
          <Route path="/equipment/pressure-control/calibrations/:equipmentId" element={<VerticalGuard vertical="well_servicing"><PressureControlGaugeScreen /></VerticalGuard>} />
          <Route path="/equipment/pressure-control/:equipmentId" element={<VerticalGuard vertical="well_servicing"><PressureControlDetailScreen /></VerticalGuard>} />
          {/* The shared radiation module (api#1334, RAD-1) for a field hand: NO vertical
              guard, because the API mounts /radiation with none — the module serves
              whichever vertical holds sources. The API lets field_worker read every
              route, record custody and handling events (check out only to themself,
              check in / transfer only what they hold) and see their own dose records.
              Same paths as the office tree. No door yet: the More row lands with
              the first consumer (WL-2 / RAD-N), so it is not shown to a tenant
              that holds no source. */}
          <Route path="/equipment/radiation" element={<RadiationScreen />} />
          <Route path="/equipment/radiation/dosimetry" element={<RadiationDosimetryScreen />} />
          <Route path="/equipment/radiation/:id" element={<RadiationSourceScreen />} />
          {/* Earthworks EW-4 — the log route before the job route. */}
          <Route path="/field/ew" element={<VerticalGuard vertical="earthworks"><EwMyJobsScreen /></VerticalGuard>} />
          <Route path="/field/ew/jobs/:id/log/:workDate" element={<VerticalGuard vertical="earthworks"><EwFieldLogScreen /></VerticalGuard>} />
          <Route path="/field/ew/jobs/:id" element={<VerticalGuard vertical="earthworks"><EwFieldJobScreen /></VerticalGuard>} />
          <Route path="/field/ew/tickets/:id" element={<VerticalGuard vertical="earthworks"><EwFieldTicketScreen /></VerticalGuard>} />
          <Route path="/field/ew/change-orders/:id" element={<VerticalGuard vertical="earthworks"><EwFieldCoScreen /></VerticalGuard>} />
          <Route path="/field/jobs" element={<CasingFieldGuard><FieldJobsScreen /></CasingFieldGuard>} />
          <Route path="/field/jobs/:jobId" element={<CasingFieldGuard><FieldJobDetail /></CasingFieldGuard>} />
          {/* The way in is the Add control on the job's Equipment card, which
              only renders when the tenant flag is on — so this is not a route
              with no nav entry, it is a sub-route of the screen that owns it.
              Same shape as the office picker at /jobs/:jobId/assign-equipment. */}
          <Route path="/field/jobs/:jobId/assign-equipment" element={<CasingFieldGuard><FieldJobEquipmentScreen /></CasingFieldGuard>} />
          <Route path="/field/tickets" element={<CasingFieldGuard><FieldTicketsScreen /></CasingFieldGuard>} />
          <Route path="/field/safety" element={<FieldSafetyScreen />} />
          <Route path="/field/safety/meeting" element={<Navigate to="/forms/pre-job-meeting" replace />} />
          <Route path="/field/safety/jsa/:id/edit" element={<JsaEditPage />} />
          <Route path="/field/safety/jsa" element={
            <SafetyFormPage title="Job Safety Analysis" Form={JSAForm} apiCall={(d) => api.safety.createJsa(d)} transform={transformJsaData} formType="jsa" submitAfterCreate={(id) => api.safety.submitJsa(id)} />
          } />
          {/* Casing only, like the list below: the form writes
              /casing/inspection-submissions, which 403s without casing_running. */}
          <Route path="/field/safety/inspection" element={
            <CasingFieldGuard><InspectionFormPage /></CasingFieldGuard>
          } />
          {/* A hand's own inspections. THIS TREE IS THE ONLY ONE THAT NEEDS
              them: `/safety/records` is registered in the AppLayout tree
              below, so a casing field worker submitting an inspection used to
              land on this tree's catch-all and be redirected to /field —
              record filed, screen gone. See lib/inspection-surface.ts.

              Specific before `:id`: `/edit` is a segment, and registered
              under the detail path it would otherwise be read as a UUID. */}
          <Route path="/field/safety/inspections" element={<CasingFieldGuard><FieldInspectionsScreen /></CasingFieldGuard>} />
          <Route path="/field/safety/inspections/:id/edit" element={<CasingFieldGuard><FieldInspectionResumeScreen /></CasingFieldGuard>} />
          <Route path="/field/safety/inspections/:id" element={<CasingFieldGuard><InspectionDetailPage /></CasingFieldGuard>} />
          <Route path="/field/safety/incident" element={
            <SafetyFormPage title="Report Incident" Form={IncidentReportForm} apiCall={(d) => api.safety.createIncident(d)} transform={transformIncidentData} formType="incident" />
          } />
          <Route path="/field/time-off" element={<FieldTimeOffScreen />} />
          {/* A hand's receipts. Reached from More and from a job's action bar
              (which passes ?job=). Specific before anything that could read
              "new" as an id — there is no /:id here today, but the order is
              the rule. Online-only: no SyncMutation entity, by decision. */}
          <Route path="/field/expenses" element={<FieldExpensesScreen />} />
          <Route path="/field/expenses/new" element={<FieldExpenseCaptureScreen />} />
          <Route path="/field/more" element={<FieldMoreScreen />} />
          {/* The casing tree's own Forms library. It reads /forms/available
              rather than /field/forms, which needs a driver record this
              persona does not have. Reached from the More menu and from a
              job's action bar — a route with no way in is invisible. */}
          <Route path="/field/forms" element={<FieldFormsScreen />} />
          <Route path="/field/equipment" element={<FieldEquipmentScreen />} />
          <Route path="/field/equipment/:id" element={<FieldEquipmentDetail />} />
          <Route path="/field/equipment/check" element={<FieldEquipmentCheckForm />} />
          <Route path="/field/certs" element={<FieldCertsScreen />} />
          <Route path="/field/pay" element={<FieldPayScreen />} />
          <Route path="/field/clock-in" element={<ClockInScreen />} />
          <Route path="/field/clock-in/shift/:timesheetId/edit" element={<OwnShiftEditScreen />} />
          <Route path="/field/clock-in/missed-shift" element={<MissedShiftScreen />} />
          <Route path="/field/profile" element={<FieldProfileScreen />} />

          {/* ── Shared /forms destinations ──
              Registered in every tree that can reach one. These are not
              duplicates of the same paths in the /my-work tree above or the
              AppLayout tree below: AppRoutes returns exactly ONE of these
              <Routes> blocks per user, so a path missing here is missing for
              casing field workers specifically. Both were, and the catch-all
              below silently redirected them to /field — a tap that reads as
              broken rather than unbuilt.

              /forms/pre-job-meeting is the target of this tree's OWN redirect
              at /field/safety/meeting above, which is the sharper version of
              the bug: the tree pointed at a route it did not have, so the
              redirect resolved to the catch-all and the Safety Meeting tile
              landed the user back on the home screen. */}
          <Route path="/forms/dynamic/:templateId" element={<DynamicSafetyFormScreen />} />
          <Route path="/forms/pre-job-meeting" element={<PreJobMeetingForm />} />

          {/* ── Notification preferences ── */}
          <Route path="/field/settings/notifications" element={<NotificationPreferencesScreen />} />
          {/* The header's AI button (fe#815). Only the office tree routed /agent,
              so here it fell through to the catch-all and did nothing. */}
          <Route path="/agent" element={<FieldAgentPage />} />

          {/* ── Compliance training (shared /safety paths) ── */}
          <Route path="/safety/my-training" element={<MyTrainingScreen />} />
          <Route path="/safety/training/:courseId/take" element={<TakeTrainingScreen />} />

          <Route path="/login" element={<PostLoginRedirect fallback="/field" />} />
          <Route path="*" element={<Navigate to="/field" replace />} />
        </Routes>
        {/* Search, for a field hand whose tree can open what it finds (fe#815). */}
        <GlobalSearch />
      </div>
    );
  }

  return (
    <>
      <Routes>
        <Route element={<AppLayout />}>
        {/* A sales user may open only the routes in lib/sales-access.ts; every
            other path redirects to SALES_HOME. A no-op for every other role. */}
        <Route element={<SalesRouteGate />}>
        {/* Home: the vertical's landing page (HomeRedirect) */}
        <Route index element={<HomeRedirect />} />

        {/* Casing vertical */}
        <Route path="/jobs" element={<VerticalGuard vertical="casing_running"><JobsScreen /></VerticalGuard>} />
        <Route path="/dispatch" element={<VerticalGuard vertical="casing_running"><DispatchScreen /></VerticalGuard>} />
        <Route path="/tickets" element={<VerticalGuard vertical="casing_running"><TicketsScreen /></VerticalGuard>} />
        <Route path="/casing/intake" element={<VerticalGuard vertical="casing_running"><CasingIntakeScreen /></VerticalGuard>} />

        {/* Trucking vertical */}
        {/* The trucking dashboard is retired (fix/trucking-nav), as /rentals was: one
          * Overview for every vertical, whose Trucking Dispatch widget is the copy that stays. */}
        <Route path="/trucking" element={<Navigate to="/" replace />} />
        {/* Sweep batch 9 (#803): the Console, trucking Intake and the rentals Customer
          * Portal pages are deleted. /trucking/console redirects to Dispatch, which is
          * its list view now. /trucking/intake and /rentals/portal have no successor and
          * fall to this tree's catch-all, which lands on the tenant's home. */}
        <Route path="/trucking/console" element={<Navigate to="/trucking/dispatch" replace />} />
        <Route path="/trucking/dispatch" element={<VerticalGuard vertical="trucking"><TruckingDispatch /></VerticalGuard>} />
        <Route path="/trucking/calendar" element={<VerticalGuard vertical="trucking"><DispatchCalendar /></VerticalGuard>} />
        <Route path="/trucking/new-load" element={<VerticalGuard vertical="trucking"><NewLoadBooking /></VerticalGuard>} />
        <Route path="/trucking/move-jobs" element={<VerticalGuard vertical="trucking"><MoveJobsScreen /></VerticalGuard>} />
        {/* The trucking rate card (api#950). Nav: VERTICAL_NAV `trucking-rates`, and HomeScreen's trucking group. */}
        <Route path="/trucking/rates" element={<VerticalGuard vertical="trucking"><TruckingRatesScreen /></VerticalGuard>} />
        {/* One trucking invoice. Way in: its row on Finance → Invoices (finance-invoice-verticals.ts). */}
        <Route path="/trucking/invoices/:id" element={<VerticalGuard vertical="trucking"><TruckingInvoiceDetail /></VerticalGuard>} />
        <Route path="/trucking/move-jobs/:id" element={<VerticalGuard vertical="trucking"><MoveJobDetailScreen /></VerticalGuard>} />
        <Route path="/trucking/weight-tickets" element={<VerticalGuard vertical="trucking"><WeightTicketView /></VerticalGuard>} />
        {/* pages/TruckingSettings.tsx was a Lovable mock: no API call anywhere in
          * it, and Save was `toast.success("Settings saved")` over local state.
          * The live page is /settings/trucking, which persists through
          * PATCH /settings/trucking. Redirected rather than dropped so the
          * links to it still land. */}
        <Route path="/trucking/settings" element={<Navigate to="/settings/trucking" replace />} />
        {/* /trucking/invoice/:bolId is gone with pages/InvoiceDraft.tsx. The screen
          * made no API call at all, ignored the :bolId it was handed, and rendered
          * one hardcoded invoice -- a named operator, a named driver, a truck unit,
          * a BOL reference and an $1,850 float haul, none of which belong to any
          * tenant -- identically for everyone. Nothing replaces it: there is no
          * invoice-from-a-load screen. The real invoice surface is /invoices and
          * /invoices/:invoiceId. */}
        <Route path="/trucking/fleet" element={<VerticalGuard vertical={HAULING_VERTICALS}><TrucksScreen /></VerticalGuard>} />
        <Route path="/trucking/fleet/:id" element={<VerticalGuard vertical={HAULING_VERTICALS}><FleetUnitPage /></VerticalGuard>} />
        <Route path="/trucking/trip-inspections" element={<VerticalGuard vertical={HAULING_VERTICALS}><TripInspectionsScreen /></VerticalGuard>} />
        <Route path="/trucking/trip-inspections/new" element={<VerticalGuard vertical={HAULING_VERTICALS}><TripInspectionNewPage /></VerticalGuard>} />
        <Route path="/trucking/trip-inspections/:id" element={<VerticalGuard vertical={HAULING_VERTICALS}><TripInspectionDetailPage /></VerticalGuard>} />
        <Route path="/trucking/trucks" element={<Navigate to="/trucking/fleet" replace />} />
        <Route path="/trucking/fleet-expenses" element={<Navigate to="/trucking/fleet?tab=expenses" replace />} />
        <Route path="/trucking/drivers" element={<VerticalGuard vertical={HAULING_VERTICALS}><DriverManagementScreen /></VerticalGuard>} />
        <Route path="/trucking/drivers/:id" element={<VerticalGuard vertical={HAULING_VERTICALS}><DriverPage /></VerticalGuard>} />
        <Route path="/trucking/payroll" element={<Navigate to="/payroll" replace />} />
        <Route path="/trucking/reports" element={<VerticalGuard vertical="trucking"><TruckingReportsScreen /></VerticalGuard>} />
        <Route path="/trucking/loads/:loadId/bol" element={<VerticalGuard vertical="trucking"><LoadBolRedirect /></VerticalGuard>} />
        <Route path="/trucking/bols" element={<VerticalGuard vertical="trucking"><BillsOfLading /></VerticalGuard>} />
        <Route path="/trucking/bols/:id" element={<VerticalGuard vertical="trucking"><BillOfLadingDetail /></VerticalGuard>} />

        {/* The office tree used to mount seventeen /driver/* routes, labelled
          * "admin viewing driver detail pages". Nothing in the office app linked
          * to any of them: the only way in was a typed URL, and the only links
          * between them were the driver screens' own DriverBottomNav. They drew
          * phone layouts inside the desktop chrome, under two bottom bars. A
          * driver (field_worker / driver role) gets the /my-work tree above,
          * where the /driver/* screens a driver actually uses are mounted. */}

        {/* Surface Rentals vertical */}
        {/* The Rentals dashboard is retired (2026-10-15): ONE Overview for every
          * vertical, and its figures are the Overview's Rentals Summary widget. The
          * old link, bookmarks and the casing sidebar's rentals entry land there. */}
        <Route path="/rentals" element={<Navigate to="/" replace />} />
        <Route path="/rentals/dispatch" element={<VerticalGuard vertical="surface_rentals"><RentalDispatchBoard /></VerticalGuard>} />
        <Route path="/rentals/active" element={<VerticalGuard vertical="surface_rentals"><ActiveRentals /></VerticalGuard>} />
        <Route path="/rentals/orders/:id" element={<VerticalGuard vertical="surface_rentals"><RentalOrderDetail /></VerticalGuard>} />
        <Route path="/rentals/invoices/:id" element={<VerticalGuard vertical="surface_rentals"><RentalInvoiceDetail /></VerticalGuard>} />
        <Route path="/rentals/dispatches/:id" element={<VerticalGuard vertical="surface_rentals"><RentalDispatchDetail /></VerticalGuard>} />
        <Route path="/rentals/assets" element={<VerticalGuard vertical="surface_rentals"><AssetRegistry /></VerticalGuard>} />
        {/* A row on Rental Equipment opens the asset on its own page; nothing opens
          * over the list (split panes are banned). The way in is the list's nav entry. */}
        <Route path="/rentals/assets/:assetId" element={<VerticalGuard vertical="surface_rentals"><RentalAssetDetailScreen /></VerticalGuard>} />
        <Route path="/rentals/yard" element={<VerticalGuard vertical="surface_rentals"><RentalYard /></VerticalGuard>} />
        <Route path="/rentals/tickets" element={<VerticalGuard vertical="surface_rentals"><RentalFieldTickets /></VerticalGuard>} />
        <Route path="/rentals/tickets/:id" element={<VerticalGuard vertical="surface_rentals"><RentalFieldTicketDetail /></VerticalGuard>} />
        <Route path="/rentals/maintenance" element={<VerticalGuard vertical="surface_rentals"><MaintenanceWorkOrders /></VerticalGuard>} />
        <Route path="/rentals/maintenance/:id" element={<VerticalGuard vertical="surface_rentals"><RentalWorkOrderDetail /></VerticalGuard>} />
        {/* The rentals report library and its two children are deleted. Eight of
          * its ten cards did nothing, and the other two opened these children:
          * one with every button disabled, one whose totals string-concatenated
          * NUMERIC columns. The working rentals reports (revenue summary, revenue
          * by asset, yard movement, maintenance summary) are rows in the /reports
          * catalog. Redirected rather than dropped so an old link lands there. */}
        <Route path="/rentals/reports/*" element={<Navigate to="/reports" replace />} />
        <Route path="/rentals/price-books" element={<VerticalGuard vertical="surface_rentals"><PriceBooks /></VerticalGuard>} />
        <Route path="/rentals/inspections" element={<VerticalGuard vertical="surface_rentals"><RentalInspections /></VerticalGuard>} />
        <Route path="/rentals/inspections/new" element={<VerticalGuard vertical="surface_rentals"><RentalInspectionDraft /></VerticalGuard>} />
        <Route path="/rentals/inspections/:id" element={<VerticalGuard vertical="surface_rentals"><RentalInspectionDetail /></VerticalGuard>} />
        <Route path="/rentals/damage-charges" element={<VerticalGuard vertical="surface_rentals"><RentalDamageCharges /></VerticalGuard>} />
        {/* The old rentals settings screen was a second, diverging settings UI
          * that nothing linked to. Its one section with no live equivalent —
          * rental billing rules — is not mounted anywhere, and nothing reads
          * those rules (see RentalsSettingsPage), so this redirect loses
          * nothing. The file itself was deleted on 2026-09-18;
          * it took the only Verticals toggle UI (PATCH /companies/verticals/toggle)
          * with it, and that toggle is recoverable from git history. See
          * CLAUDE.md, Settings pages. */}
        <Route path="/rentals/settings" element={<Navigate to="/settings/rentals" replace />} />

        {/* Finance — unified invoices */}
        <Route path="/finance/invoices" element={<FinanceInvoices />} />

        {/* Downhole Tools — rebuilt on the dh_* tables (fieldiq-api#882 reads,
          * #883 Assembly Builder writes). Every route is behind a POSITIVE
          * downhole_tools guard; the API refuses the namespace for any other
          * tenant anyway (requireVertical).
          *
          * Every downhole screen is routed (src/pages/downhole/README.md). The
          * run-log FORM and the disassembly screen were deleted: both were
          * write-only, onto endpoints the API has not built. A route goes in
          * with its nav entry, never ahead of it. */}
        <Route path="/downhole" element={<VerticalGuard vertical="downhole_tools"><DownholeDashboardScreen /></VerticalGuard>} />
        <Route path="/downhole/components" element={<VerticalGuard vertical="downhole_tools"><DownholeComponentsScreen /></VerticalGuard>} />
        <Route path="/downhole/components/:id" element={<VerticalGuard vertical="downhole_tools"><DownholeComponentDetailScreen /></VerticalGuard>} />
        <Route path="/downhole/assemblies" element={<VerticalGuard vertical="downhole_tools"><DownholeAssembliesScreen /></VerticalGuard>} />
        {/* Directional & MWD well jobs (api#1055). The screens gate on the dd_mwd mode themselves. */}
        <Route path="/downhole/wells" element={<VerticalGuard vertical="downhole_tools"><DownholeWellsScreen /></VerticalGuard>} />
        <Route path="/downhole/wells/:id" element={<VerticalGuard vertical="downhole_tools"><DownholeWellDetailScreen /></VerticalGuard>} />
        {/* `:id` is `new` for a new assembly; reached from Assemblies and Templates. */}
        <Route path="/downhole/assemblies/:id/build" element={<VerticalGuard vertical="downhole_tools"><DownholeAssemblyBuilderScreen /></VerticalGuard>} />
        {/* Reached from the builder's Tear down button on a laid_down assembly. */}
        <Route path="/downhole/assemblies/:id/teardown" element={<VerticalGuard vertical="downhole_tools"><DownholeTeardownScreen /></VerticalGuard>} />
        <Route path="/downhole/templates" element={<VerticalGuard vertical="downhole_tools"><DownholeTemplatesScreen /></VerticalGuard>} />
        <Route path="/downhole/run-logs" element={<VerticalGuard vertical="downhole_tools"><DownholeRunLogsScreen /></VerticalGuard>} />
        <Route path="/downhole/work-orders" element={<VerticalGuard vertical="downhole_tools"><DownholeWorkOrdersScreen /></VerticalGuard>} />
        {/* A row on each of these three lists opens the record on its own page; no
          * list carries a detail panel beside it (split panes are banned). The way
          * in is the list row, which every one of them has in the nav. */}
        <Route path="/downhole/templates/:templateId" element={<VerticalGuard vertical="downhole_tools"><DownholeTemplateDetailScreen /></VerticalGuard>} />
        <Route path="/downhole/run-logs/:runId" element={<VerticalGuard vertical="downhole_tools"><DownholeRunLogDetailScreen /></VerticalGuard>} />
        <Route path="/downhole/work-orders/:workOrderId" element={<VerticalGuard vertical="downhole_tools"><DownholeWorkOrderDetailScreen /></VerticalGuard>} />
        <Route path="/downhole/tickets" element={<VerticalGuard vertical="downhole_tools"><DownholeTicketsScreen /></VerticalGuard>} />
        <Route path="/downhole/tickets/:id" element={<VerticalGuard vertical="downhole_tools"><DownholeTicketDetailScreen /></VerticalGuard>} />
        <Route path="/downhole/invoices" element={<VerticalGuard vertical="downhole_tools"><DownholeInvoicesScreen /></VerticalGuard>} />
        <Route path="/downhole/invoices/ready" element={<VerticalGuard vertical="downhole_tools"><DownholeReadyToInvoiceScreen /></VerticalGuard>} />
        <Route path="/downhole/invoices/:id" element={<VerticalGuard vertical="downhole_tools"><DownholeInvoiceDetailScreen /></VerticalGuard>} />
        <Route path="/downhole/reports" element={<VerticalGuard vertical="downhole_tools"><DownholeReportsScreen /></VerticalGuard>} />
        <Route path="/downhole/rates" element={<VerticalGuard vertical="downhole_tools"><DownholeRatesScreen /></VerticalGuard>} />

        {/* Pressure Testing (fieldiq-api#907). Every route has its nav entry in verticalNav.ts. */}
        <Route path="/pressure-testing" element={<Navigate to="/pressure-testing/instruments" replace />} />
        <Route path="/pressure-testing/instruments" element={<VerticalGuard vertical="pressure_testing"><PtInstrumentsScreen /></VerticalGuard>} />
        <Route path="/pressure-testing/instruments/:id" element={<VerticalGuard vertical="pressure_testing"><PtInstrumentDetailScreen /></VerticalGuard>} />
        <Route path="/pressure-testing/alerts" element={<VerticalGuard vertical="pressure_testing"><PtCalibrationAlertsScreen /></VerticalGuard>} />
        <Route path="/pressure-testing/jobs" element={<VerticalGuard vertical="pressure_testing"><PtTestJobsScreen /></VerticalGuard>} />
        <Route path="/pressure-testing/jobs/:id" element={<VerticalGuard vertical="pressure_testing"><PtTestJobDetailScreen /></VerticalGuard>} />
        <Route path="/pressure-testing/certificates" element={<VerticalGuard vertical="pressure_testing"><PtCertificatesScreen /></VerticalGuard>} />
        <Route path="/pressure-testing/tickets" element={<VerticalGuard vertical="pressure_testing"><PtFieldTicketsScreen /></VerticalGuard>} />
        <Route path="/pressure-testing/invoices" element={<VerticalGuard vertical="pressure_testing"><PtInvoicesScreen /></VerticalGuard>} />
        {/* A row on each of these three registers opens the record on its own page;
          * no drawer or panel opens over or beside the list (split panes are
          * banned). The way in is the list, which each has in the nav. */}
        <Route path="/pressure-testing/certificates/:certificateId" element={<VerticalGuard vertical="pressure_testing"><PtCertificateDetailScreen /></VerticalGuard>} />
        <Route path="/pressure-testing/tickets/:ticketId" element={<VerticalGuard vertical="pressure_testing"><PtFieldTicketDetailScreen /></VerticalGuard>} />
        <Route path="/pressure-testing/invoices/:invoiceId" element={<VerticalGuard vertical="pressure_testing"><PtInvoiceDetailScreen /></VerticalGuard>} />
        {/* NDT / Inspections batch 1 (fieldiq-api#1134). Full-width registers, each row opening its own page. */}
        <Route path="/ndt-inspection" element={<Navigate to="/ndt-inspection/technicians" replace />} />
        <Route path="/ndt-inspection/technicians" element={<VerticalGuard vertical="ndt_inspection"><NdtTechniciansScreen /></VerticalGuard>} />
        <Route path="/ndt-inspection/technicians/certs/:id" element={<VerticalGuard vertical="ndt_inspection"><NdtTechnicianCertDetailScreen /></VerticalGuard>} />
        <Route path="/ndt-inspection/welders" element={<VerticalGuard vertical="ndt_inspection"><NdtWeldersScreen /></VerticalGuard>} />
        <Route path="/ndt-inspection/welders/:id" element={<VerticalGuard vertical="ndt_inspection"><NdtWelderDetailScreen /></VerticalGuard>} />
        <Route path="/ndt-inspection/instruments" element={<VerticalGuard vertical="ndt_inspection"><NdtInstrumentsScreen /></VerticalGuard>} />
        <Route path="/ndt-inspection/instruments/:id" element={<VerticalGuard vertical="ndt_inspection"><NdtInstrumentDetailScreen /></VerticalGuard>} />
        <Route path="/ndt-inspection/sources" element={<VerticalGuard vertical="ndt_inspection"><NdtSourcesScreen /></VerticalGuard>} />
        <Route path="/ndt-inspection/sources/:id" element={<VerticalGuard vertical="ndt_inspection"><NdtSourceDetailScreen /></VerticalGuard>} />
        <Route path="/ndt-inspection/dosimetry" element={<VerticalGuard vertical="ndt_inspection"><NdtDosimetryScreen /></VerticalGuard>} />
        {/* NDT batch 2 (fieldiq-api#1144). NDT jobs are their own table — not casing jobs. */}
        <Route path="/ndt-inspection/jobs" element={<VerticalGuard vertical="ndt_inspection"><NdtJobsScreen /></VerticalGuard>} />
        <Route path="/ndt-inspection/jobs/:id" element={<VerticalGuard vertical="ndt_inspection"><NdtJobDetailScreen /></VerticalGuard>} />
        <Route path="/ndt-inspection/welds" element={<VerticalGuard vertical="ndt_inspection"><NdtWeldsScreen /></VerticalGuard>} />
        <Route path="/ndt-inspection/welds/:id" element={<VerticalGuard vertical="ndt_inspection"><NdtWeldDetailScreen /></VerticalGuard>} />
        <Route path="/ndt-inspection/reports" element={<VerticalGuard vertical="ndt_inspection"><NdtReportsScreen /></VerticalGuard>} />
        <Route path="/ndt-inspection/reports/:id" element={<VerticalGuard vertical="ndt_inspection"><NdtReportDetailScreen /></VerticalGuard>} />
        <Route path="/ndt-inspection/tickets" element={<VerticalGuard vertical="ndt_inspection"><NdtTicketsScreen /></VerticalGuard>} />
        <Route path="/ndt-inspection/tickets/:id" element={<VerticalGuard vertical="ndt_inspection"><NdtTicketDetailScreen /></VerticalGuard>} />
        {/* Batch 5 (fieldiq-api#1166): approved tickets → NDI- invoices. */}
        <Route path="/ndt-inspection/invoices" element={<VerticalGuard vertical="ndt_inspection"><NdtInvoicesScreen /></VerticalGuard>} />
        <Route path="/ndt-inspection/invoices/:id" element={<VerticalGuard vertical="ndt_inspection"><NdtInvoiceDetailScreen /></VerticalGuard>} />
        {/* Batch 6 AI digests (fieldiq-api#1177 / #1178): the register and one digest's page. */}
        <Route path="/ndt-inspection/ai" element={<VerticalGuard vertical="ndt_inspection"><NdtAiDigestsScreen /></VerticalGuard>} />
        <Route path="/ndt-inspection/ai/:id" element={<VerticalGuard vertical="ndt_inspection"><NdtAiDigestDetailScreen /></VerticalGuard>} />
        {/* Pipeline & Facility PF-1 (fieldiq-api#1196). Full-width register; a site opens on its own page. /new before /:id. */}
        <Route path="/pipeline-facility" element={<Navigate to="/pipeline-facility/sites" replace />} />
        <Route path="/pipeline-facility/sites" element={<VerticalGuard vertical="pipeline_facility"><PfSitesScreen /></VerticalGuard>} />
        <Route path="/pipeline-facility/sites/new" element={<VerticalGuard vertical="pipeline_facility"><PfNewSiteScreen /></VerticalGuard>} />
        <Route path="/pipeline-facility/sites/:id" element={<VerticalGuard vertical="pipeline_facility"><PfSiteDetailScreen /></VerticalGuard>} />
        {/* PF-2 (fieldiq-api#1200): jobs and daily logs. Specific before `:id`. */}
        <Route path="/pipeline-facility/jobs" element={<VerticalGuard vertical="pipeline_facility"><PfJobsScreen /></VerticalGuard>} />
        <Route path="/pipeline-facility/jobs/new" element={<VerticalGuard vertical="pipeline_facility"><PfNewJobScreen /></VerticalGuard>} />
        <Route path="/pipeline-facility/jobs/:jobId/daily-logs/new" element={<VerticalGuard vertical="pipeline_facility"><PfDailyLogScreen /></VerticalGuard>} />
        <Route path="/pipeline-facility/jobs/:id" element={<VerticalGuard vertical="pipeline_facility"><PfJobDetailScreen /></VerticalGuard>} />
        <Route path="/pipeline-facility/daily-logs/:id" element={<VerticalGuard vertical="pipeline_facility"><PfDailyLogScreen /></VerticalGuard>} />
        {/* PF-3a digs (fieldiq-api#1219). Static paths before their :id siblings. */}
        <Route path="/pipeline-facility/anomalies" element={<VerticalGuard vertical="pipeline_facility"><PfAnomaliesScreen /></VerticalGuard>} />
        <Route path="/pipeline-facility/anomalies/new" element={<VerticalGuard vertical="pipeline_facility"><PfNewAnomalyScreen /></VerticalGuard>} />
        <Route path="/pipeline-facility/anomalies/import" element={<VerticalGuard vertical="pipeline_facility"><PfAnomalyImportScreen /></VerticalGuard>} />
        <Route path="/pipeline-facility/anomalies/:id" element={<VerticalGuard vertical="pipeline_facility"><PfAnomalyDetailScreen /></VerticalGuard>} />
        <Route path="/pipeline-facility/digs" element={<VerticalGuard vertical="pipeline_facility"><PfDigsScreen /></VerticalGuard>} />
        <Route path="/pipeline-facility/digs/:id" element={<VerticalGuard vertical="pipeline_facility"><PfDigDetailScreen /></VerticalGuard>} />
        <Route path="/pipeline-facility/jobs/:id/digs/new" element={<VerticalGuard vertical="pipeline_facility"><PfNewDigScreen /></VerticalGuard>} />
        <Route path="/pipeline-facility/welds" element={<VerticalGuard vertical="pipeline_facility"><PfWeldsScreen /></VerticalGuard>} />
        <Route path="/pipeline-facility/welds/:id" element={<VerticalGuard vertical="pipeline_facility"><PfWeldDetailScreen /></VerticalGuard>} />
        <Route path="/pipeline-facility/jobs/:id/welds/new" element={<VerticalGuard vertical="pipeline_facility"><PfNewWeldScreen /></VerticalGuard>} />
        <Route path="/pipeline-facility/dig-reports" element={<VerticalGuard vertical="pipeline_facility"><PfReportsScreen /></VerticalGuard>} />
        <Route path="/pipeline-facility/rates" element={<VerticalGuard vertical="pipeline_facility"><PfRatesScreen /></VerticalGuard>} />
        <Route path="/pipeline-facility/rates/new" element={<VerticalGuard vertical="pipeline_facility"><PfNewRateScreen /></VerticalGuard>} />
        <Route path="/pipeline-facility/rates/:id" element={<VerticalGuard vertical="pipeline_facility"><PfRateDetailScreen /></VerticalGuard>} />
        <Route path="/pipeline-facility/tickets" element={<VerticalGuard vertical="pipeline_facility"><PfTicketsScreen /></VerticalGuard>} />
        <Route path="/pipeline-facility/tickets/:id" element={<VerticalGuard vertical="pipeline_facility"><PfTicketDetailScreen /></VerticalGuard>} />
        <Route path="/pipeline-facility/invoices" element={<VerticalGuard vertical="pipeline_facility"><PfInvoicesScreen /></VerticalGuard>} />
        <Route path="/pipeline-facility/invoices/ready" element={<VerticalGuard vertical="pipeline_facility"><PfReadyToInvoiceScreen /></VerticalGuard>} />
        <Route path="/pipeline-facility/invoices/:id" element={<VerticalGuard vertical="pipeline_facility"><PfInvoiceDetailScreen /></VerticalGuard>} />
        <Route path="/pipeline-facility/dig-reports/:id" element={<VerticalGuard vertical="pipeline_facility"><PfReportDetailScreen /></VerticalGuard>} />
        {/* PF-6 (fieldiq-api#1236): the AI Drafts register and one draft's page. */}
        <Route path="/pipeline-facility/ai" element={<VerticalGuard vertical="pipeline_facility"><PfAiDraftsScreen /></VerticalGuard>} />
        <Route path="/pipeline-facility/ai/:id" element={<VerticalGuard vertical="pipeline_facility"><PfAiDraftDetailScreen /></VerticalGuard>} />
        {/* Well Testing WT-1 (fieldiq-api#1241). /new before /:id. */}
        <Route path="/well-testing" element={<Navigate to="/well-testing/wells" replace />} />
        <Route path="/well-testing/wells" element={<VerticalGuard vertical="well_testing"><WtWellsScreen /></VerticalGuard>} />
        <Route path="/well-testing/wells/new" element={<VerticalGuard vertical="well_testing"><WtNewWellScreen /></VerticalGuard>} />
        <Route path="/well-testing/wells/:id" element={<VerticalGuard vertical="well_testing"><WtWellDetailScreen /></VerticalGuard>} />
        {/* WT-2 (fieldiq-api#1245): packages, jobs, shift logs. Specific paths before :id. */}
        <Route path="/well-testing/packages" element={<VerticalGuard vertical="well_testing"><WtPackagesScreen /></VerticalGuard>} />
        <Route path="/well-testing/packages/new" element={<VerticalGuard vertical="well_testing"><WtPackageDetailScreen /></VerticalGuard>} />
        <Route path="/well-testing/packages/:id" element={<VerticalGuard vertical="well_testing"><WtPackageDetailScreen /></VerticalGuard>} />
        <Route path="/well-testing/jobs" element={<VerticalGuard vertical="well_testing"><WtJobsScreen /></VerticalGuard>} />
        <Route path="/well-testing/jobs/new" element={<VerticalGuard vertical="well_testing"><WtNewJobScreen /></VerticalGuard>} />
        <Route path="/well-testing/jobs/:jobId/shift-logs/new" element={<VerticalGuard vertical="well_testing"><WtShiftLogScreen /></VerticalGuard>} />
        <Route path="/well-testing/jobs/:id/readings/import" element={<VerticalGuard vertical="well_testing"><WtLoggerImportScreen /></VerticalGuard>} />
        <Route path="/well-testing/jobs/:id" element={<VerticalGuard vertical="well_testing"><WtJobDetailScreen /></VerticalGuard>} />
        <Route path="/well-testing/shift-logs/:id" element={<VerticalGuard vertical="well_testing"><WtShiftLogScreen /></VerticalGuard>} />
        <Route path="/well-testing/rates" element={<VerticalGuard vertical="well_testing"><WtRatesScreen /></VerticalGuard>} />
        <Route path="/well-testing/rates/new" element={<VerticalGuard vertical="well_testing"><WtNewRateScreen /></VerticalGuard>} />
        <Route path="/well-testing/rates/:id" element={<VerticalGuard vertical="well_testing"><WtRateDetailScreen /></VerticalGuard>} />
        <Route path="/well-testing/tickets" element={<VerticalGuard vertical="well_testing"><WtTicketsScreen /></VerticalGuard>} />
        <Route path="/well-testing/tickets/:id" element={<VerticalGuard vertical="well_testing"><WtTicketDetailScreen /></VerticalGuard>} />
        <Route path="/well-testing/invoices" element={<VerticalGuard vertical="well_testing"><WtInvoicesScreen /></VerticalGuard>} />
        <Route path="/well-testing/invoices/new" element={<VerticalGuard vertical="well_testing"><WtNewInvoiceScreen /></VerticalGuard>} />
        <Route path="/well-testing/invoices/:id" element={<VerticalGuard vertical="well_testing"><WtInvoiceDetailScreen /></VerticalGuard>} />
        {/* WT-6 (fieldiq-api#1261): the AI Drafts register and one draft's page. */}
        <Route path="/well-testing/ai" element={<VerticalGuard vertical="well_testing"><WtAiDraftsScreen /></VerticalGuard>} />
        <Route path="/well-testing/ai/:id" element={<VerticalGuard vertical="well_testing"><WtAiDraftDetailScreen /></VerticalGuard>} />
        {/* Earthworks EW-1. /new before /:id. */}
        <Route path="/earthworks" element={<Navigate to="/earthworks/jobs" replace />} />
        <Route path="/earthworks/jobs" element={<VerticalGuard vertical="earthworks"><EwJobsScreen /></VerticalGuard>} />
        <Route path="/earthworks/jobs/new" element={<VerticalGuard vertical="earthworks"><EwNewJobScreen /></VerticalGuard>} />
        <Route path="/earthworks/jobs/:id" element={<VerticalGuard vertical="earthworks"><EwJobDetailScreen /></VerticalGuard>} />
        <Route path="/earthworks/employees/:userId/hours" element={<VerticalGuard vertical="earthworks"><EwEmployeeHoursScreen /></VerticalGuard>} />
        <Route path="/earthworks/sites" element={<VerticalGuard vertical="earthworks"><EwSitesScreen /></VerticalGuard>} />
        <Route path="/earthworks/sites/new" element={<VerticalGuard vertical="earthworks"><EwNewSiteScreen /></VerticalGuard>} />
        <Route path="/earthworks/sites/:id" element={<VerticalGuard vertical="earthworks"><EwSiteDetailScreen /></VerticalGuard>} />
        <Route path="/earthworks/rates" element={<VerticalGuard vertical="earthworks"><EwRatesScreen /></VerticalGuard>} />
        <Route path="/earthworks/inventory" element={<VerticalGuard vertical="earthworks"><EwInventoryScreen /></VerticalGuard>} />
        <Route path="/earthworks/inventory/new" element={<VerticalGuard vertical="earthworks"><EwNewMaterialScreen /></VerticalGuard>} />
        <Route path="/earthworks/inventory/locations" element={<VerticalGuard vertical="earthworks"><EwMaterialLocationsScreen /></VerticalGuard>} />
        <Route path="/earthworks/inventory/locations/:id" element={<VerticalGuard vertical="earthworks"><EwMaterialLocationScreen /></VerticalGuard>} />
        <Route path="/earthworks/inventory/:id" element={<VerticalGuard vertical="earthworks"><EwMaterialDetailScreen /></VerticalGuard>} />
        <Route path="/earthworks/rates/new" element={<VerticalGuard vertical="earthworks"><EwNewRateScreen /></VerticalGuard>} />
        <Route path="/earthworks/rates/:id" element={<VerticalGuard vertical="earthworks"><EwRateDetailScreen /></VerticalGuard>} />
        {/* EW-3 AI quoting. /new before /:id, or "new" is read as an id. */}
        <Route path="/earthworks/ai-quotes" element={<VerticalGuard vertical="earthworks"><EwAiQuotesScreen /></VerticalGuard>} />
        <Route path="/earthworks/ai-quotes/new" element={<VerticalGuard vertical="earthworks"><EwNewAiQuoteScreen /></VerticalGuard>} />
        <Route path="/earthworks/ai-quotes/:id" element={<VerticalGuard vertical="earthworks"><EwAiQuoteDetailScreen /></VerticalGuard>} />
        {/* EW-8 quoted vs actual: the job types, then one job type's summary. */}
        <Route path="/earthworks/quoted-vs-actual" element={<VerticalGuard vertical="earthworks"><EwQvaScreen /></VerticalGuard>} />
        <Route path="/earthworks/quoted-vs-actual/:jobType" element={<VerticalGuard vertical="earthworks"><EwQvaJobTypeScreen /></VerticalGuard>} />
        <Route path="/earthworks/daily-logs/:id" element={<VerticalGuard vertical="earthworks"><EwDailyLogScreen /></VerticalGuard>} />
        <Route path="/earthworks/jobs/:id/log/:workDate" element={<VerticalGuard vertical="earthworks"><EwOfficeLogEditScreen /></VerticalGuard>} />
        <Route path="/earthworks/tickets" element={<VerticalGuard vertical="earthworks"><EwTicketsScreen /></VerticalGuard>} />
        <Route path="/earthworks/tickets/:id" element={<VerticalGuard vertical="earthworks"><EwTicketDetailScreen /></VerticalGuard>} />
        <Route path="/earthworks/change-orders" element={<VerticalGuard vertical="earthworks"><EwChangeOrdersScreen /></VerticalGuard>} />
        <Route path="/earthworks/change-orders/:id" element={<VerticalGuard vertical="earthworks"><EwChangeOrderDetailScreen /></VerticalGuard>} />
        {/* EW-6: the Ready to invoice queue and one invoice. The invoice LIST is Finance → Invoices. */}
        <Route path="/earthworks/invoices" element={<VerticalGuard vertical="earthworks"><EwReadyToInvoiceScreen /></VerticalGuard>} />
        <Route path="/earthworks/invoices/:id" element={<VerticalGuard vertical="earthworks"><EwInvoiceDetailScreen /></VerticalGuard>} />

        {/* Well Servicing WS-1. /new before /:id. */}
        <Route path="/well-servicing" element={<Navigate to="/well-servicing/wells" replace />} />
        <Route path="/well-servicing/wells" element={<VerticalGuard vertical="well_servicing"><WsWellsScreen /></VerticalGuard>} />
        <Route path="/well-servicing/wells/new" element={<VerticalGuard vertical="well_servicing"><WsNewWellScreen /></VerticalGuard>} />
        <Route path="/well-servicing/wells/:id" element={<VerticalGuard vertical="well_servicing"><WsWellDetailScreen /></VerticalGuard>} />
        <Route path="/well-servicing/rigs" element={<VerticalGuard vertical="well_servicing"><WsRigsScreen /></VerticalGuard>} />
        <Route path="/well-servicing/rigs/new" element={<VerticalGuard vertical="well_servicing"><WsNewRigScreen /></VerticalGuard>} />
        <Route path="/well-servicing/rigs/:id" element={<VerticalGuard vertical="well_servicing"><WsRigDetailScreen /></VerticalGuard>} />
        <Route path="/well-servicing/activity-codes" element={<VerticalGuard vertical="well_servicing"><WsActivityCodesScreen /></VerticalGuard>} />
        <Route path="/well-servicing/jobs" element={<VerticalGuard vertical="well_servicing"><WsJobsScreen /></VerticalGuard>} />
        <Route path="/well-servicing/jobs/new" element={<VerticalGuard vertical="well_servicing"><WsNewJobScreen /></VerticalGuard>} />
        <Route path="/well-servicing/jobs/:id" element={<VerticalGuard vertical="well_servicing"><WsJobDetailScreen /></VerticalGuard>} />
        <Route path="/well-servicing/tours/:id" element={<VerticalGuard vertical="well_servicing"><WsTourScreen /></VerticalGuard>} />
        <Route path="/well-servicing/rates" element={<VerticalGuard vertical="well_servicing"><WsRatesScreen /></VerticalGuard>} />
        <Route path="/well-servicing/rates/new" element={<VerticalGuard vertical="well_servicing"><WsNewRateScreen /></VerticalGuard>} />
        <Route path="/well-servicing/rates/:id" element={<VerticalGuard vertical="well_servicing"><WsRateDetailScreen /></VerticalGuard>} />
        <Route path="/well-servicing/tickets" element={<VerticalGuard vertical="well_servicing"><WsTicketsScreen /></VerticalGuard>} />
        <Route path="/well-servicing/tickets/:id" element={<VerticalGuard vertical="well_servicing"><WsTicketDetailScreen /></VerticalGuard>} />
        <Route path="/well-servicing/invoices" element={<VerticalGuard vertical="well_servicing"><WsInvoicesScreen /></VerticalGuard>} />
        <Route path="/well-servicing/invoices/new" element={<VerticalGuard vertical="well_servicing"><WsNewInvoiceScreen /></VerticalGuard>} />
        {/* The API's action queue links "Ready to invoice" here (fieldiq-api lib/action-queue/well-servicing.ts). Before :id, or it reads "ready" as an id. */}
        <Route path="/well-servicing/invoices/ready" element={<VerticalGuard vertical="well_servicing"><WsReadyToInvoiceScreen /></VerticalGuard>} />
        <Route path="/well-servicing/invoices/:id" element={<VerticalGuard vertical="well_servicing"><WsInvoiceDetailScreen /></VerticalGuard>} />
        <Route path="/well-servicing/ai" element={<VerticalGuard vertical="well_servicing"><WsAiDraftsScreen /></VerticalGuard>} />
        <Route path="/well-servicing/ai/:id" element={<VerticalGuard vertical="well_servicing"><WsAiDraftDetailScreen /></VerticalGuard>} />

        {/* Wireline & Slickline WL-1 (fieldiq-api#1336). /new before /:id. */}
        <Route path="/wireline-slickline" element={<VerticalGuard vertical="wireline_slickline"><WlOverviewScreen /></VerticalGuard>} />
        <Route path="/wireline-slickline/wells" element={<VerticalGuard vertical="wireline_slickline"><WlWellsScreen /></VerticalGuard>} />
        <Route path="/wireline-slickline/wells/new" element={<VerticalGuard vertical="wireline_slickline"><WlNewWellScreen /></VerticalGuard>} />
        <Route path="/wireline-slickline/wells/:id" element={<VerticalGuard vertical="wireline_slickline"><WlWellDetailScreen /></VerticalGuard>} />
        <Route path="/wireline-slickline/units" element={<VerticalGuard vertical="wireline_slickline"><WlUnitsScreen /></VerticalGuard>} />
        <Route path="/wireline-slickline/units/new" element={<VerticalGuard vertical="wireline_slickline"><WlNewUnitScreen /></VerticalGuard>} />
        <Route path="/wireline-slickline/units/:id" element={<VerticalGuard vertical="wireline_slickline"><WlUnitDetailScreen /></VerticalGuard>} />
        <Route path="/wireline-slickline/lines" element={<VerticalGuard vertical="wireline_slickline"><WlLinesScreen /></VerticalGuard>} />
        <Route path="/wireline-slickline/lines/new" element={<VerticalGuard vertical="wireline_slickline"><WlNewLineScreen /></VerticalGuard>} />
        <Route path="/wireline-slickline/lines/:id" element={<VerticalGuard vertical="wireline_slickline"><WlLineDetailScreen /></VerticalGuard>} />
        <Route path="/wireline-slickline/tool-strings" element={<VerticalGuard vertical="wireline_slickline"><WlToolStringsScreen /></VerticalGuard>} />
        <Route path="/wireline-slickline/tool-strings/new" element={<VerticalGuard vertical="wireline_slickline"><WlNewToolStringScreen /></VerticalGuard>} />
        <Route path="/wireline-slickline/tool-strings/:id" element={<VerticalGuard vertical="wireline_slickline"><WlToolStringDetailScreen /></VerticalGuard>} />
        <Route path="/wireline-slickline/jobs" element={<VerticalGuard vertical="wireline_slickline"><WlJobsScreen /></VerticalGuard>} />
        <Route path="/wireline-slickline/jobs/new" element={<VerticalGuard vertical="wireline_slickline"><WlNewJobScreen /></VerticalGuard>} />
        <Route path="/wireline-slickline/jobs/:id" element={<VerticalGuard vertical="wireline_slickline"><WlJobDetailScreen /></VerticalGuard>} />
        <Route path="/wireline-slickline/sheets/:id" element={<VerticalGuard vertical="wireline_slickline"><WlSheetScreen /></VerticalGuard>} />
        <Route path="/wireline-slickline/sheets/:id/runs/new" element={<VerticalGuard vertical="wireline_slickline"><WlNewRunScreen /></VerticalGuard>} />
        <Route path="/wireline-slickline/runs/:id" element={<VerticalGuard vertical="wireline_slickline"><WlRunScreen /></VerticalGuard>} />
        <Route path="/wireline-slickline/reports" element={<VerticalGuard vertical="wireline_slickline"><WlReportsScreen /></VerticalGuard>} />
        <Route path="/wireline-slickline/reports/:id" element={<VerticalGuard vertical="wireline_slickline"><WlReportDetailScreen /></VerticalGuard>} />
        <Route path="/wireline-slickline/rates" element={<VerticalGuard vertical="wireline_slickline"><WlRatesScreen /></VerticalGuard>} />
        <Route path="/wireline-slickline/rates/new" element={<VerticalGuard vertical="wireline_slickline"><WlNewRateScreen /></VerticalGuard>} />
        <Route path="/wireline-slickline/rates/:id" element={<VerticalGuard vertical="wireline_slickline"><WlRateDetailScreen /></VerticalGuard>} />
        <Route path="/wireline-slickline/tickets" element={<VerticalGuard vertical="wireline_slickline"><WlTicketsScreen /></VerticalGuard>} />
        <Route path="/wireline-slickline/tickets/:id" element={<VerticalGuard vertical="wireline_slickline"><WlTicketDetailScreen /></VerticalGuard>} />
        {/* WL has no invoice register of its own: invoices list in Finance → Invoices. The old path still lands somewhere. */}
        <Route path="/wireline-slickline/invoices" element={<VerticalGuard vertical="wireline_slickline"><Navigate to="/finance/invoices" replace /></VerticalGuard>} />
        <Route path="/wireline-slickline/invoices/ready" element={<VerticalGuard vertical="wireline_slickline"><WlReadyToInvoiceScreen /></VerticalGuard>} />
        <Route path="/wireline-slickline/invoices/new" element={<VerticalGuard vertical="wireline_slickline"><WlNewInvoiceScreen /></VerticalGuard>} />
        <Route path="/wireline-slickline/invoices/:id" element={<VerticalGuard vertical="wireline_slickline"><WlInvoiceDetailScreen /></VerticalGuard>} />
        <Route path="/wireline-slickline/ai" element={<VerticalGuard vertical="wireline_slickline"><WlAiDraftsScreen /></VerticalGuard>} />
        <Route path="/wireline-slickline/ai/:id" element={<VerticalGuard vertical="wireline_slickline"><WlAiDraftDetailScreen /></VerticalGuard>} />

        {/* Safety — one band and one section strip over six pages (SafetyLayout).
          * PATHLESS, so every child keeps its URL, `/cert-review` included:
          * reviewer notifications deep-link to it with `?highlight=`. It sits
          * outside the /safety namespace and under the strip anyway.
          * Cross-vertical — deliberately no hasVertical guard. */}
        <Route element={<SafetyLayout />}>
          <Route path="/safety" element={<SafetyScreen />} />
          <Route path="/safety/records" element={<SafetyRecordsScreen />} />
          <Route path="/safety/metrics" element={<SafetyMetricsScreen />} />
          <Route path="/safety/training" element={<ComplianceDashboard />} />
          <Route path="/safety/my-training" element={<MyTrainingScreen />} />
          {/* Certification review queue. The route is not role-gated; the
            * strip's link to it is, to the reviewer roles. */}
          <Route path="/cert-review" element={<CertificationReviewQueue />} />
        </Route>
        <Route path="/safety/training/courses" element={<CourseManagement />} />
        {/* A course's form on its own page, opened from Course Management (no drawer
          * over the list). `new` is listed before the id route. */}
        <Route path="/safety/training/courses/new" element={<CourseEditScreen />} />
        <Route path="/safety/training/courses/:courseId/edit" element={<CourseEditScreen />} />
        <Route path="/safety/training/:courseId/take" element={<TakeTrainingScreen />} />
        <Route path="/safety/meeting" element={<Navigate to="/forms/pre-job-meeting" replace />} />
        <Route path="/safety/jsa/:id/edit" element={<JsaEditPage />} />
        <Route path="/safety/jsa" element={
          <SafetyFormPage title="Job Safety Analysis" Form={JSAForm} apiCall={(d) => api.safety.createJsa(d)} transform={transformJsaData} formType="jsa" submitAfterCreate={(id) => api.safety.submitJsa(id)} />
        } />
        <Route path="/safety/inspection" element={
          <InspectionFormPage />
        } />
        <Route path="/safety/incident" element={
          <SafetyFormPage title="Report Incident" Form={IncidentReportForm} apiCall={(d) => api.safety.createIncident(d)} transform={transformIncidentData} formType="incident" />
        } />
        <Route path="/safety/inspections/:id" element={<InspectionDetailPage />} />
        <Route path="/safety/team-certs" element={<TeamCertsScreen />} />
        <Route path="/safety/submissions" element={<Navigate to="/safety/records" replace />} />
        <Route path="/safety/submissions/:id" element={<SafetySubmissionDetailScreen />} />
        <Route path="/safety/form" element={<SafetyForm />} />
        {/* The form library itself is the Forms tab of the Safety workspace,
            under /settings below. These are its full-screen builders, outside
            the settings layout on purpose; each exits to /settings/safety/forms.
            Specific segments before `:id` — `/upload` and `/extractions` would
            otherwise be parsed as template ids. */}
        <Route path="/settings/safety/forms/new" element={<SafetyFormBuilderScreen />} />
        <Route path="/settings/safety/forms/upload" element={<FormReplicatorUploadScreen />} />
        <Route
          path="/settings/safety/forms/extractions/:id"
          element={<FormExtractionReviewScreen />}
        />
        <Route path="/settings/safety/forms/:id/edit" element={<SafetyFormBuilderScreen />} />
        <Route path="/forms/dynamic/:templateId" element={<DynamicSafetyFormScreen />} />
        <Route path="/forms/pre-job-meeting" element={<PreJobMeetingForm />} />

        {/* More menu */}
        <Route path="/more" element={<MoreScreen />} />

        {/* Settings — nested layout with sidebar */}
        <Route path="/settings" element={<SettingsLayout />}>
          <Route index element={<SettingsIndexPage />} />
          {/* Workspaces. Each is a pathless layout route, so every page keeps
            * the URL it had before workspaces existed and nothing redirects;
            * the workspace only adds a tab strip above it. The groupings and the
            * tab order live in settingsNavConfig.ts — the same list the sidebar
            * and the mobile hub read. */}
          <Route element={<SettingsWorkspaceTabs workspace="organization" titled />}>
            <Route path="company-profile" element={<CompanyProfilePage />} />
            <Route path="locations" element={<LocationsPage />} />
            <Route path="appearance" element={<AppearancePage />} />
            <Route path="email-branding" element={<EmailBrandingPage />} />
            <Route path="dashboard" element={<DashboardLayoutPage />} />
          </Route>
          <Route element={<SettingsWorkspaceTabs workspace="notifications" titled />}>
            <Route path="notifications" element={<NotificationPreferencesScreen />} />
          </Route>
          <Route element={<SettingsWorkspaceTabs workspace="finance" titled />}>
            <Route path="regional-tax" element={<RegionalTaxPage />} />
            <Route path="invoice" element={<InvoiceSettingsPage />} />
            <Route path="pay-periods" element={<PayPeriodSettingsScreen />} />
          </Route>
          <Route element={<SettingsWorkspaceTabs workspace="operations" titled />}>
            <Route path="field-tickets" element={<FieldTicketsPage />} />
            <Route path="job-numbering" element={<JobNumberingPage />} />
            <Route path="services" element={<ServicesPage />} />
            <Route path="line-item-types" element={<LineItemTypesPage />} />
            <Route path="equipment-types" element={<EquipmentTypesPage />} />
            <Route path="trucking" element={<TruckingSettingsPage />} />
            <Route path="rentals" element={<RentalsSettingsPage />} />
            <Route path="pressure-testing" element={<PressureTestingSettingsPage />} />
            <Route path="ndt-inspection" element={<NdtSettingsPage />} />
            <Route path="pipeline-facility" element={<PipelineFacilitySettingsPage />} />
            <Route path="well-testing" element={<WellTestingSettingsPage />} />
            <Route path="pressure-control" element={<PressureControlSettingsPage />} />
            <Route path="radiation" element={<RadiationSettingsPage />} />
            <Route path="earthworks" element={<EarthworksSettingsPage />} />
            <Route path="well-servicing" element={<WellServicingSettingsPage />} />
            <Route path="wireline-slickline" element={<WirelineSlicklineSettingsPage />} />
            <Route path="downhole" element={<DownholeSettingsPage />} />
          </Route>
          {/* Retired in favour of the Numbering section on the Field Tickets page.
            * Redirect rather than drop, so a bookmark from #376 still lands. */}
          <Route path="ticket-numbering" element={<Navigate to="/settings/field-tickets" replace />} />
          {/* Safety workspace: one tab per URL, so the two pre-workspace URLs
            * are the tab URLs and still land. The form builders stay full-screen
            * at top level, above. Bare /settings/safety opens the first tab. */}
          <Route path="safety" element={<SafetyWorkspace />}>
            <Route index element={<Navigate to="/settings/safety/metrics" replace />} />
            <Route path="metrics" element={<SafetyMetricsSettingsPage />} />
            <Route path="forms" element={<SafetyFormTemplatesScreen />} />
          </Route>
          <Route element={<SettingsWorkspaceTabs workspace="people" titled />}>
            <Route path="team/roles" element={<RolesPermissionsPage />} />
            <Route path="positions" element={<PositionsPage />} />
          </Route>
          {/* Members was a read-only copy of /team and was dropped after fe#690 and fe#691.
            * Redirected rather than dropped, so a bookmark still lands. */}
          <Route path="team/members" element={<Navigate to="/team" replace />} />
          {/* Trucking positions moved to the Trucking tab: they are trucking
            * data. Redirected rather than dropped, so a bookmark still lands. */}
          <Route path="team/positions" element={<Navigate to="/settings/trucking" replace />} />
          <Route element={<SettingsWorkspaceTabs workspace="ai-integrations" titled />}>
            <Route path="ai-agent" element={<AIAgentSettingsPage />} />
            <Route path="billing-agent" element={<BillingAgentPage />} />
            <Route path="scout-agent" element={<ScoutAgentPage />} />
            <Route path="ai-usage" element={<AiUsagePage />} />
            <Route path="maintenance-agent" element={<MaintenanceAgentPage />} />
            <Route path="integrations" element={<IntegrationsPage />} />
            {/* A drill-in from the Integrations tab, which stays selected. */}
            <Route path="integrations/quickbooks-mapping" element={<QuickBooksMappingPage />} />
            <Route path="integrations/gps" element={<GpsConnectorsPage />} />
            <Route path="api-keys" element={<SettingsPlaceholder title="API Keys" />} />
            <Route path="learned-rules" element={<LearnedRulesPage />} />
          </Route>
          <Route element={<SettingsWorkspaceTabs workspace="activity" titled />}>
            <Route path="audit-log" element={<SettingsPlaceholder title="Audit Log" />} />
          </Route>
          <Route path="company" element={<Navigate to="/settings/company-profile" replace />} />
        </Route>

        {/* Jobs */}
        <Route path="/jobs/trash" element={<DeletedJobsScreen />} />
        <Route path="/jobs/new" element={<NewJobScreen key="new" />} />
        <Route path="/jobs/:jobId/edit" element={<NewJobScreen key="edit" />} />
        <Route path="/jobs/:jobId/assign-crew" element={<CrewAssignmentScreen />} />
        <Route path="/jobs/:jobId/assign-equipment" element={<EquipmentAssignmentScreen />} />

        {/* Invoice screens */}
        <Route path="/invoices" element={<InvoicesScreen />} />
        <Route path="/invoices/generate" element={<InvoiceGenerateScreen />} />
        <Route path="/invoices/:invoiceId" element={<InvoiceDetailScreen />} />
        <Route path="/invoices/:invoiceId/edit" element={<InvoiceEditScreen />} />

        {/* Quote screens */}
        <Route path="/quotes" element={<QuotesScreen />} />
        <Route path="/quotes/create" element={<QuoteCreateScreen />} />
        <Route path="/quotes/:quoteId" element={<QuoteDetailScreen />} />
        <Route path="/quotes/:quoteId/edit" element={<QuoteEditScreen />} />

        {/* AI Agent */}
        <Route path="/agent" element={<AIAgentScreen />} />

        {/* Alerts */}
        <Route path="/alerts" element={<AlertsScreen />} />

        {/* Equipment, team, payroll, and shared screens */}
        <Route path="/equipment" element={<EquipmentScreen />} />
        <Route path="/equipment/:equipmentId" element={<EquipmentDetail />} />
        <Route path="/equipment/certs" element={<EquipmentCertScreen />} />
        {/* Pre-use forms per equipment type (api#1209). Cross-vertical like the
            rest of Equipment — no vertical guard. Reached from the Equipment
            band and Settings → Equipment Types. */}
        {/* Pressure control — shared BOP certificates (api#1268). Cross-vertical
            like Equipment, no vertical guard; reached from the band on /equipment
            and the panel on an Equipment detail page. */}
        <Route path="/equipment/pressure-control" element={<PressureControlScreen />} />
        <Route path="/equipment/pressure-control/tests" element={<PressureControlTestsScreen />} />
        <Route path="/equipment/pressure-control/tests/new" element={<PressureControlTestNewScreen />} />
        <Route path="/equipment/pressure-control/tests/:testId" element={<PressureControlTestDetailScreen />} />
        <Route path="/equipment/pressure-control/drills" element={<PressureControlDrillsScreen />} />
        <Route path="/equipment/pressure-control/drills/:drillId" element={<PressureControlDrillDetailScreen />} />
        <Route path="/equipment/pressure-control/calibrations" element={<PressureControlCalibrationsScreen />} />
        <Route path="/equipment/pressure-control/calibrations/:equipmentId" element={<PressureControlGaugeScreen />} />
        <Route path="/equipment/pressure-control/:equipmentId" element={<PressureControlDetailScreen />} />
        {/* Radiation — the shared sealed-source module (api#1334, RAD-1). Like
            Pressure control: no vertical guard; reached from the band on /equipment. */}
        <Route path="/equipment/radiation" element={<RadiationScreen />} />
        <Route path="/equipment/radiation/dosimetry" element={<RadiationDosimetryScreen />} />
        <Route path="/equipment/radiation/:id" element={<RadiationSourceScreen />} />
        <Route path="/equipment/preuse-templates" element={<PreuseTemplatesScreen />} />
        <Route path="/equipment/preuse-templates/new" element={<PreuseTemplateNewScreen />} />
        {/* Build a pre-use template from a scanned/PDF checklist with the AI form
            replicator (api#1210): the replicator's own upload and review screens,
            in pre-use mode. Approval makes a DRAFT; publishing stays on the
            template's page. */}
        <Route path="/equipment/preuse-templates/from-document" element={<FormReplicatorUploadScreen target="preuse_template" />} />
        <Route path="/equipment/preuse-templates/extractions/:id" element={<FormExtractionReviewScreen />} />
        <Route path="/equipment/preuse-templates/:templateId" element={<PreuseTemplateDetailScreen />} />
        {/* Reached from the Import button on /equipment/certs — a route with no
            nav entry is invisible. Cross-vertical, like the rest of Equipment,
            so no vertical guard; the role gate is the screen's and the API's. */}
        <Route path="/equipment/certs/import" element={<CertDayImportScreen />} />
        <Route path="/yards" element={<ServiceLocationsScreen />} />
        <Route path="/time-off" element={<TimeOffScreen />} />
        <Route path="/team" element={<TeamScreen />} />
        <Route path="/team/import" element={<Navigate to="/import/employees" replace />} />
        <Route path="/import/:entityType" element={<ImportScreen />} />
        <Route path="/payroll" element={<PayrollScreen />} />
        <Route path="/payroll/clock-in" element={<ClockInScreen />} />
        <Route path="/payroll/clock-in/shift/:timesheetId/edit" element={<OwnShiftEditScreen />} />
        <Route path="/payroll/clock-in/missed-shift" element={<MissedShiftScreen />} />
        <Route path="/payroll/approvals" element={<ApprovalQueueScreen />} />
        <Route path="/payroll/approvals/:timesheetId" element={<TimesheetApprovalScreen />} />
        <Route path="/payroll/workers" element={<WorkerListScreen />} />
        <Route path="/payroll/workers/:userId/pay-profile" element={<WorkerPayProfileScreen />} />
        <Route path="/payroll/summary" element={<PayrollSummaryScreen />} />
        {/* Payroll Reports moved into the Timesheets screen as its "Timesheet reports"
            half. Redirected, not removed: bookmarks and old links still land. */}
        <Route path="/payroll/reports" element={<Navigate to="/payroll/approvals?view=reports" replace />} />
        {/* Office approval of field receipts. Nav entry: casingNav FINANCE. Not
            in either field tree, so a field-role user's catch-all bounces it. */}
        {/* FE-1: an office user files their own receipt — job or General. */}
        <Route path="/expenses/new" element={<OfficeExpenseCaptureScreen />} />
        <Route path="/expenses" element={<ExpenseApprovalScreen />} />
        <Route path="/customers" element={<CustomersScreen />} />
        {/* The CRM (CRM-FE-2..4). CRM_ROLES only — the API gates every call
            behind these pages the same way, so any other role is told it has
            no access rather than shown pages of 403s. Nav: casingNav SALES. */}
        <Route element={<CrmRouteGate />}>
          <Route path="/sales/meetings" element={<MeetingsScreen />} />
          <Route path="/sales/meetings/new" element={<MeetingFormScreen />} />
          <Route path="/sales/meetings/:meetingId" element={<MeetingDetail />} />
          <Route path="/sales/meetings/:meetingId/edit" element={<MeetingFormScreen />} />
          <Route path="/sales/pipeline" element={<PipelineScreen />} />
          <Route path="/sales/pipeline/:opportunityId" element={<OpportunityDetail />} />
        </Route>
        <Route path="/rates" element={<RateSheetsScreen />} />
        {/* Price-book FE-2. Way in: the Rate Sheets band's "Missing from book" action. */}
        <Route path="/rates/missing" element={<PriceBookMissingScreen />} />

        {/* Reports */}
        <Route path="/reports" element={<UnifiedReports />} />
        {/* Hours & Jobs by Employee -> one employee (api#1193). Reached from the
            report's rows, not the nav: the report is the way in. */}
        <Route path="/reports/employee-hours/:userId" element={<EmployeeHoursDetailScreen />} />
        <Route path="/reports/customers" element={<CustomerReportScreen />} />
        <Route path="/reports/equipment-maintenance" element={<EquipmentMaintenanceReportScreen />} />
        <Route path="/reports/dashboard" element={<ReportsDashboard />} />
        {/*
          SEVEN RETIRED REPORT PAGES AND ONE RETIRED CATALOG, ALL REDIRECTED TO
          THE /reports CATALOG THAT ALREADY RUNS THEM (fe#761, then fe#762).

          ── The four from fe#761 ──────────────────────────────────────────────

          Each rendered a payload shape `GET /reports/by-*` has never sent, and
          each had NO inbound link anywhere in this repo — not the /reports
          catalog, not ReportsScreen, not a nav entry. The only way to any of
          them was typing the URL, which is why nothing reported that
          /reports/well printed three $NaN, /reports/date-range was structurally
          $0, and /reports/equipment-util threw on `status.replace()` and left a
          white screen.

            /reports/well            -> "Tickets & Revenue by Rig"
            /reports/date-range      -> "Tickets & Revenue by Date"
            /reports/equipment-util  -> "Equipment Utilization Report"
            /reports/crew            -> "Hours & Jobs by Employee"

          ── The three from fe#762 ─────────────────────────────────────────────

          These three were NOT linked from nothing — they were linked from the
          OTHER catalog, `screens/ReportsScreen.tsx` at /safety/reports, which
          this PR deletes. That is the whole reason they are coupled: deleting
          the second catalog is what makes them unreachable, so they go in the
          same change rather than being left as three more URL-only pages of
          the kind fe#761 had to clean up.

            /safety/reports    -> the catalog itself. A SECOND report catalog,
                                  routed but linked from nothing — no nav entry
                                  in casingNav, HomeScreen or any vertical
                                  sidebar. Its 18 rows are all accounted for:
                                  14 have rows in UnifiedReports' catalog,
                                  `fleet-expenses` already redirects into the
                                  Fleet screen's Expenses tab (which IS in the
                                  trucking nav), two were `route: '#'`
                                  placeholders that never navigated anywhere,
                                  and the last two are the pages below.

            /reports/customer  -> "Revenue by Customer". The page read
                                  GET /reports/by-customer, which is exactly
                                  what the catalog's `rev-customer` row runs,
                                  under the same title. The catalog's runner
                                  adds a range picker, sorting and CSV export;
                                  the page had a three-preset pill row and no
                                  error branch at all — a refused read rendered
                                  "No revenue data for this period."

            /reports/equipment -> "Equipment Utilization Report". The page was
                                  TITLED "Equipment Utilization" and did not
                                  compute utilization: it read
                                  `api.equipment.list()` — the equipment
                                  register — and tallied statuses client-side.
                                  So the question it claimed to answer is the
                                  catalog's `ops-equipment` row, which reads the
                                  API's own GET /reports/by-equipment
                                  (deployment days, jobs, utilization rates),
                                  and the question it actually answered is
                                  /equipment's, which does it properly: eight
                                  status chips against this page's four, and an
                                  em-dash fallback where this page echoed the
                                  raw enum value, so `reserved` and `retired`
                                  rendered as the bare strings "reserved" and
                                  "retired".

          Redirects rather than deletions-to-the-catch-all: the catch-all lands
          on "/", so a stale bookmark would arrive at the home dashboard with no
          hint that the report still exists. Same idiom as /payroll/reports
          above. Pinned by `pages/reports/retired-report-routes.test.tsx`,
          controls included.
        */}
        <Route path="/reports/well" element={<Navigate to="/reports" replace />} />
        <Route path="/reports/equipment-util" element={<Navigate to="/reports" replace />} />
        <Route path="/reports/date-range" element={<Navigate to="/reports" replace />} />
        <Route path="/reports/crew" element={<Navigate to="/reports" replace />} />
        <Route path="/reports/customer" element={<Navigate to="/reports" replace />} />
        <Route path="/reports/equipment" element={<Navigate to="/reports" replace />} />
        <Route path="/safety/reports" element={<Navigate to="/reports" replace />} />
        <Route path="/reports/financial" element={<CasingFinancialReport />} />
        <Route path="/reports/revenue-summary" element={<RevenueSummaryReport />} />
        <Route path="/reports/revenue-by-asset" element={<RevenueByAssetReport />} />
        <Route path="/reports/customer-unified" element={<CrossCustomerReport />} />
        <Route path="/reports/yard-movement" element={<YardMovementReport />} />
        <Route path="/reports/maintenance-summary" element={<MaintenanceSummaryReport />} />
        <Route path="/reports/payroll-summary" element={<PayrollSummaryReport />} />
        <Route path="/reports/driver-pay-detail" element={<DriverPayDetailReport />} />

        {/* Ticket creation & pay */}
        <Route path="/tickets/new" element={<NewTicketScreen />} />
        <Route path="/tickets/:ticketId/edit" element={<NewTicketScreen />} />
        <Route path="/tickets/:ticketId/pay" element={<PayAssignmentScreen />} />

        {/* Detail pages */}
        <Route path="/jobs/:jobId" element={<JobDetail />} />
        <Route path="/tickets/:ticketId" element={<TicketDetail />} />
        <Route path="/team/:employeeId" element={<EmployeeProfile />} />
        <Route path="/customers/:customerId" element={<CustomerDetail />} />
        <Route path="/rate-sheets/:rateSheetId" element={<RateSheetDetail />} />

        {/* Help Videos */}
        <Route path="/help-videos" element={<HelpVideosScreen />} />

        {/* Training */}
        <Route path="/training" element={<TrainingDashboard />} />
        <Route path="/training/course/:courseId" element={<CourseDetail />} />
        <Route path="/training/video/:videoId" element={<TrainingVideoPlayer />} />
        <Route path="/training/troubleshooting" element={<TroubleshootingGuide />} />
        <Route path="/training/troubleshooting/:guideId" element={<TroubleshootingGuide />} />
        {/*
          CertificationTracking is deleted. It rendered entirely STATIC fabricated
          compliance records — named people with completion percentages, certificate
          earned/expiry dates, "H2S Alive — current" — with no query behind it at all.
          Nothing linked to it; `training` is not in CASING_SIDEBAR_STRUCTURE, so it was
          reachable only by URL.

          This redirect is NOT optional tidiness, and it is not about where it sits in
          this list. Deleting the route alone does not 404 and never reaches the
          catch-all: `/training/:videoId` matches "certifications" and renders
          VideoPlayer, which is hardcoded to one invented video ("Power Tongs Setup")
          for every id it is given. Removing one fabrication would have handed the path
          to another, and the only visible difference would have been the wording.

          Position is irrelevant here — react-router ranks a static segment above a
          dynamic one regardless of declaration order, verified against this tree by
          moving this line below the `:videoId` entry and watching nothing change. The
          backend rule in CLAUDE.md ("specific routes before `:id`") is an Express rule
          and does not transfer. What matters is only that this route EXISTS.

          The real screen is /safety/my-training, which reads
          /safety/training/my-certifications.
        */}
        <Route path="/training/certifications" element={<Navigate to="/safety/my-training" replace />} />
        <Route path="/training/:videoId" element={<VideoPlayer />} />

        {/* The ticket-reminder SMS links /field/jobs/:id, the field app's
            screen. An office reader opening it gets the office job page for the
            same id rather than the catch-all's Overview. */}
        <Route path="/field/jobs/:jobId" element={<FieldJobLinkRedirect />} />
        <Route path="/login" element={<PostLoginRedirect fallback="/" />} />
        <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
        </Route>
      </Routes>
      <BottomNav />
      <ChatNotificationToast />
      <GlobalChatToast notification={globalChat.latestNotification} onDismiss={globalChat.clearNotification} />
      <GlobalSearch />
    </>
  );
}

export function SyncInit() {
  const { isAuthenticated, user } = useAuth();
  // Sales is an employee but is refused /sync (api#1032's allowlist), so it
  // reads its screens online and does not pull.
  const isSyncClient = isInternalRole(user?.role) && !isSalesRole(user?.role);

  useEffect(() => {
    if (!isAuthenticated) return;
    // Only the tenant's own people are sync clients. The API gates /sync on
    // its INTERNAL_ROLES allowlist (api#808) and refuses everyone else 403 —
    // a portal user today, any role added later. Mirrored as the same
    // allowlist, not as `!isCustomer`, so an unknown role is skipped here too
    // rather than drawing a 403 every five minutes. See `isInternalRole`.
    if (!isSyncClient) return;

    initOnlineDetection();

    // `performSync` rejects when the pull fails, so `lastSyncAt` is stamped
    // only on the success path — but the counters are refreshed either way,
    // in `finally`. A failed sync is exactly when something may have landed in
    // the failed queue, and that is the moment the red bar has to appear.
    const runSync = () => {
      const store = useSyncStore.getState();
      store.setSyncing(true);
      performSync()
        .then((result) => {
          store.setLastSyncAt(result.timestamp);
          store.setConflicts(result.pushed.conflicts);
          store.setError(null);
          store.setServerUnreachable(false);
        })
        // Recorded, not swallowed: SyncStatusBar reads `error` and stops saying
        // "Synced …" over a sync that just failed.
        .catch((err: any) => store.setError(err?.message || 'Sync failed'))
        .finally(() => {
          store.setSyncing(false);
          void refreshSyncCounts();
          void store.loadConflictDetails();
        });
    };

    // Initial sync on app start
    if (navigator.onLine) runSync();

    // Periodic sync every 5 minutes
    const interval = setInterval(() => {
      if (navigator.onLine && !useSyncStore.getState().isSyncing) runSync();
    }, 5 * 60 * 1000);

    // Detection is torn down with the effect. It runs `performSync` on every
    // reconnect, so leaving it attached would keep syncing for whoever signs in
    // next on this device — including a portal user the guard above skips.
    return () => {
      clearInterval(interval);
      destroyOnlineDetection();
    };
  }, [isAuthenticated, isSyncClient]);

  return null;
}

function CapacitorInit() {
  useEffect(() => {
    if (!isNative) return;

    StatusBar.setStyle({ style: Style.Dark }).catch(() => {});
    StatusBar.setBackgroundColor({ color: '#0f172a' }).catch(() => {});
    SplashScreen.hide().catch(() => {});

    const backHandler = CapApp.addListener('backButton', ({ canGoBack }) => {
      if (canGoBack) window.history.back();
      else CapApp.exitApp();
    });

    return () => { backHandler.then(h => h.remove()); };
  }, []);

  return null;
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <LocaleProvider>
          <IndustryProvider>
              <ThemeProvider>
              <Router>
              <CapacitorInit />
              <SyncInit />
              <PushInit />
              <AppRoutes />
              <PushPrompt />
              <Toaster position="top-right" richColors />
            </Router>
            </ThemeProvider>
          </IndustryProvider>
        </LocaleProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
